/**
 * PropertiesPanel — right sidebar. Routes by what's selected:
 *   - Nothing      → DefaultPanel (project info + equipment summary)
 *   - Title block  → ProjectInfoForm
 *   - Inverter     → InverterEditor (model swap + remove)
 *   - Battery      → BatteryEditor (model swap + remove)
 *   - Panel string → PanelEditor (model swap)
 *   - Anything else (DC iso, fuse, meter, etc) → generic symbol editor
 */
import React, { useState, useMemo } from 'react';
import { useEditorStore, type SldNode, type SldEdge } from '../store/editor';
import type { Topology, InverterType, BatteryCoupling } from '../types/topology';
import { SYMBOLS, renderPanelString } from '../symbols/library';
import { getBrandLogoUrl, onBrandLogosLoaded } from '../catalog/brandLogos';
import type { Rotation } from '../symbols/placement';
import { INVERTER_CATALOG, inverterFromCatalog, type InverterCatalogEntry } from '../catalog/inverters';
import { PANEL_CATALOG, type PanelCatalogEntry } from '../catalog/panels';
import { BATTERY_CATALOG, batteryFromCatalog, type BatteryCatalogEntry } from '../catalog/batteries';

interface Props {
  selectedNode: SldNode | null;
  /** All currently-selected user nodes (excluding chrome). When length > 1
   *  the panel switches to MultiSelectPanel for bulk operations. */
  selectedNodes?: SldNode[];
  /** Single selected wire (edge), when no nodes are selected. Routes
   *  to WireEditor so the user can add a cable label / delete the wire. */
  selectedEdge?: SldEdge | null;
}

export function PropertiesPanel({ selectedNode, selectedNodes, selectedEdge }: Props) {
  const setNodes = useEditorStore((s) => s.setNodes);
  const setEdges = useEditorStore((s) => s.setEdges);
  const pushHistory = useEditorStore((s) => s.pushHistory);

  // Multi-select takes precedence — when more than one user symbol is
  // selected (typically via Cmd/Ctrl+drag box-select) the panel
  // switches to bulk-action mode rather than per-symbol editing.
  if (selectedNodes && selectedNodes.length > 1) {
    return <MultiSelectPanel selectedNodes={selectedNodes} />;
  }

  // Edge-only selection — show the wire's label editor. Placed
  // before the DefaultPanel fallback so a click on a wire opens
  // its editor instead of the project-info default.
  if (!selectedNode && selectedEdge) {
    return <WireEditor edgeId={selectedEdge.id} />;
  }

  if (!selectedNode) return <DefaultPanel />;
  if (selectedNode.id === '__title_block__') return <ProjectInfoForm />;

  // EXISTING wrapper annotation — its symbolId reuses 'panelString' for
  // SVG rendering convenience, but it's NOT a panel string. Route to its
  // own dedicated panel BEFORE the panelString check below, otherwise
  // clicking a wrapper opens PanelEditor for the global topology.panels
  // (which then looks like "the OTHER panels appeared in RHS" — confusing).
  if (selectedNode.id.startsWith('existing-boundary-')) {
    return <ExistingWrapperPanel nodeId={selectedNode.id} />;
  }

  // Panel string check.
  // Two flavours:
  //   - MANAGED: created by autoLayout from an inverter's MPPT array.
  //     Has data.topologyRef.id pointing back at the owning inverter
  //     so PanelEditor's MpptLayoutEditor can edit the inverter's
  //     mppts.
  //   - FREE-FORM: dragged from the palette. Has data.freeStringConfig
  //     storing { panels, parallels, panelTypeId } directly on the
  //     node. Edited via FreePanelStringEditor which regenerates
  //     customContent / customBox / customTerminals on each change.
  if (selectedNode.data?.symbolId === 'panelString') {
    if (selectedNode.data.freeStringConfig) {
      return <FreePanelStringEditor nodeId={selectedNode.id} />;
    }
    const ownerInvId = selectedNode.data?.topologyRef?.id;
    return <PanelEditor invId={ownerInvId} />;
  }

  const ref = selectedNode.data?.topologyRef;
  if (ref?.kind === 'inverter') return <InverterEditor invId={ref.id} />;
  if (ref?.kind === 'battery')  return <BatteryEditor batId={ref.id} />;

  const sym = SYMBOLS[selectedNode.data.symbolId];
  if (!sym) {
    return (
      <div className="props-panel">
        <div className="props-empty">
          <span className="props-hint">This element isn't editable.</span>
        </div>
      </div>
    );
  }

  const currentRotation = selectedNode.data.rotation ?? 0;

  const setRotation = (r: Rotation) => {
    pushHistory();
    setNodes((prev) =>
      prev.map((n) =>
        n.id === selectedNode.id ? { ...n, data: { ...n.data, rotation: r } } : n
      )
    );
  };

  const setLabel = (field: 'label' | 'sublabel', value: string) => {
    setNodes((prev) =>
      prev.map((n) =>
        n.id === selectedNode.id
          ? { ...n, data: { ...n.data, [field]: value } }
          : n
      )
    );
  };

  // No confirmation here — Cmd/Ctrl+Z covers reversal. The dialog
  // was friction more than safety once undo landed.
  const deleteNode = () => {
    pushHistory();
    setNodes((prev) => prev.filter((n) => n.id !== selectedNode.id));
    setEdges((prev) =>
      prev.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id)
    );
  };

  return (
    <div className="props-panel">
      <div className="props-header">{sym?.label ?? 'Unknown'}</div>
      <div className="props-sub">
        Source: {sym?.source} · ID: {selectedNode.id}
      </div>

      <div className="props-row">
        <label>Label</label>
        <input
          type="text"
          value={selectedNode.data.label ?? ''}
          onChange={(e) => setLabel('label', e.target.value)}
          onFocus={pushHistory}
          placeholder="Display label"
        />
      </div>
      <div className="props-row">
        <label>Sub-label</label>
        <input
          type="text"
          value={selectedNode.data.sublabel ?? ''}
          onChange={(e) => setLabel('sublabel', e.target.value)}
          onFocus={pushHistory}
          placeholder="e.g. 5.0 kW · 1φ"
        />
      </div>

      <div className="props-row">
        <label>Rotation</label>
        <div className="rotation-buttons">
          {([0, 90, 180, 270] as const).map((r) => (
            <button
              key={r}
              className={`rotation-btn ${currentRotation === r ? 'active' : ''}`}
              onClick={() => setRotation(r)}
            >
              {r}°
            </button>
          ))}
        </div>
      </div>

      <div className="props-row">
        <label>Position</label>
        <div className="props-position">
          x: {Math.round(selectedNode.position.x)},{' '}
          y: {Math.round(selectedNode.position.y)}
        </div>
      </div>

      <div className="props-row">
        <label>Terminals</label>
        <div className="terminals-list">
          {sym &&
            Object.values(sym.terminals).map((term) => (
              <div key={term.id} className="terminal-pill" data-type={term.type}>
                {term.id} <span className="terminal-type">{term.type}</span>
              </div>
            ))}
        </div>
      </div>

      <button className="btn-danger" onClick={deleteNode}>
        Delete Symbol
      </button>
    </div>
  );
}

// ============================================================
// PROJECT INFO + EQUIPMENT
// ============================================================

/**
 * MultiSelectPanel — shown when more than one user node is selected.
 * Bulk operations: mark as EXISTING (+ clear), rotate, delete.
 *
 * The mark-EXISTING path is two-tier:
 *   - Topology entities (inverter, battery) → set isExisting on the
 *     topology object, re-layout. Survives autoLayout regeneration.
 *   - Panel-string nodes → set isExisting on the resolved panelType
 *     (per-inverter), re-layout. Same persistence model.
 *   - Plain symbols (DC isolator, fuse, meter, etc.) → set isExisting
 *     directly on the node's data. Doesn't touch topology — fine since
 *     these symbols ARE pure visual and don't get regenerated by
 *     autoLayout once placed.
 */
function MultiSelectPanel({ selectedNodes }: { selectedNodes: SldNode[] }) {
  const setNodes = useEditorStore((s) => s.setNodes);
  const setEdges = useEditorStore((s) => s.setEdges);
  const pushHistory = useEditorStore((s) => s.pushHistory);
  const applyTopologyAndRelayout = useEditorStore((s) => s.applyTopologyAndRelayout);
  const updateTopology = useEditorStore((s) => s.updateTopology);
  const setSelectedNodeId = useEditorStore((s) => s.setSelectedNodeId);

  // Categorise the selection so the summary line + bulk handlers know
  // what they're dealing with.
  const inverterIds = new Set<string>();
  const batteryIds  = new Set<string>();
  const panelOwnerInverterIds = new Set<string>();
  const otherNodeIds = new Set<string>();
  for (const n of selectedNodes) {
    const ref = n.data?.topologyRef;
    if (ref?.kind === 'inverter' && n.data?.symbolId !== 'panelString') inverterIds.add(ref.id);
    else if (ref?.kind === 'battery') batteryIds.add(ref.id);
    else if (n.data?.symbolId === 'panelString' && ref?.kind === 'inverter') panelOwnerInverterIds.add(ref.id);
    else otherNodeIds.add(n.id);
  }

  // Compose a friendly summary like "3 selected — 1 inverter, 2 panel strings"
  const parts: string[] = [];
  if (inverterIds.size) parts.push(`${inverterIds.size} inverter${inverterIds.size > 1 ? 's' : ''}`);
  if (batteryIds.size)  parts.push(`${batteryIds.size} batter${batteryIds.size > 1 ? 'ies' : 'y'}`);
  if (panelOwnerInverterIds.size) parts.push(`panel strings on ${panelOwnerInverterIds.size} inverter${panelOwnerInverterIds.size > 1 ? 's' : ''}`);
  if (otherNodeIds.size) parts.push(`${otherNodeIds.size} other symbol${otherNodeIds.size > 1 ? 's' : ''}`);
  const summary = parts.join(', ');

  /**
   * Wrap the selection in a single dashed-violet annotation rectangle
   * sized to the selection's bounding box (with padding). This is the
   * standard SLD convention for marking a SECTION as existing — the
   * box encompasses everything inside it regardless of entity type.
   *
   * The annotation is a regular node (selectable, draggable, can be
   * deleted via Backspace). It's prepended to the nodes array so it
   * renders BEHIND the symbols inside it. Doesn't touch topology —
   * per-entity `isExisting` toggles in the per-symbol editors are
   * still available when you want to mark just one item.
   */
  const wrapSelectionAsExisting = () => {
    if (selectedNodes.length === 0) return;

    // Compute selection bbox using each node's position + measured
    // width/height (React Flow's measured dims, fall back to a rough
    // estimate when the node hasn't been measured yet).
    const PAD = 18;       // breathing room around the selection
    const LABEL_H = 14;   // extra top space for the EXISTING badge
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of selectedNodes) {
      const w = n.measured?.width  ?? (n.width  ?? 80);
      const h = n.measured?.height ?? (n.height ?? 40);
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + w);
      maxY = Math.max(maxY, n.position.y + h);
    }
    const x = minX - PAD;
    const y = minY - PAD - LABEL_H;
    const w = (maxX - minX) + PAD * 2;
    const h = (maxY - minY) + PAD * 2 + LABEL_H;

    // Build the SVG content for the boundary + label, sized to (w,h).
    const COL = '#7c3aed';
    const content =
      `<rect x="0" y="${LABEL_H}" width="${w}" height="${h - LABEL_H}" ` +
      `fill="rgba(124, 58, 237, 0.05)" stroke="${COL}" stroke-width="1.4" ` +
      `stroke-dasharray="6 4" rx="4"/>` +
      `<rect x="6" y="0" width="64" height="${LABEL_H + 2}" rx="2" fill="${COL}"/>` +
      `<text x="38" y="${LABEL_H - 2}" font-size="9" font-weight="700" ` +
      `fill="#ffffff" text-anchor="middle" letter-spacing="0.6" ` +
      `font-family="Arial">EXISTING</text>`;

    const boundaryNode: SldNode = {
      id: `existing-boundary-${Date.now().toString(36)}`,
      type: 'sldSymbol',
      position: { x, y },
      data: {
        symbolId: 'panelString',          // reuse generic composite
        customContent: content,
        customBox: { w, h },
        customTerminals: {},              // no handles
        label: '',
      },
      selectable: true,
      draggable: true,
      // Lower z so symbols render on top of the boundary
      zIndex: -1,
    } as SldNode;

    pushHistory();
    // Prepend the boundary so it renders BEHIND the symbols inside it
    // (page frame stays first; wrapper sits second; everything else
    // after). Also deselect EVERY other node and mark only the wrapper
    // selected — without this, the multi-select panel keeps the
    // previous selection alive after wrap, leading to the bug where
    // a follow-up "Wrap" button click acts on stale selection (not
    // just the wrapper the user thinks they clicked on).
    //
    // Also stamp data.isExisting=true on each affected symbol node so
    // the node state matches topology. Without this sync, removing the
    // wrapper later (Remove EXISTING box action) would leave symbols
    // un-marked in the diagram even though topology still flags them
    // as existing — the title block would say one thing and the
    // diagram another. The wrapper itself hides the per-symbol boxes
    // while it's present (insideExistingWrapper check in SldSymbolNode).
    setNodes((prev) => {
      const pageFrame = prev.find((n) => n.id === '__page_frame__');
      const stamp = (n: SldNode) => {
        const ref = n.data?.topologyRef;
        const isInverterNode =
          ref?.kind === 'inverter' &&
          n.data?.symbolId !== 'panelString' &&
          inverterIds.has(ref.id);
        const isBatteryNode =
          ref?.kind === 'battery' && batteryIds.has(ref.id);
        const isPanelStringNode =
          n.data?.symbolId === 'panelString' &&
          ref?.kind === 'inverter' &&
          panelOwnerInverterIds.has(ref.id);
        if (!isInverterNode && !isBatteryNode && !isPanelStringNode) return n;
        return { ...n, data: { ...n.data, isExisting: true } };
      };
      const rest = prev
        .filter((n) => n.id !== '__page_frame__')
        .map((n) => {
          const stamped = stamp(n);
          return stamped.selected ? { ...stamped, selected: false } : stamped;
        });
      const newBoundary = { ...boundaryNode, selected: true };
      return pageFrame
        ? [pageFrame, newBoundary, ...rest]
        : [newBoundary, ...prev];
    });
    // Also mark the topology entities inside the bbox as existing so
    // the title-block BOM picks up the EXISTING tag (violet). This
    // doesn't trigger autoLayout (we use updateTopology, not
    // applyTopologyAndRelayout) so the wrapper node and any other
    // user-positioned content survives.
    if (inverterIds.size || batteryIds.size || panelOwnerInverterIds.size) {
      useEditorStore.getState().updateTopology((t) => {
        // Inverters + batteries: per-entity flag, no shared state.
        const inverters = t.inverters.map((i) =>
          inverterIds.has(i.id) ? { ...i, isExisting: true } : i
        );
        const batteries = t.batteries.map((b) =>
          batteryIds.has(b.id) ? { ...b, isExisting: true } : b
        );
        // Panels: route through the shared helper so toggling EXISTING
        // for a subset of inverters doesn't bleed across to siblings
        // that share the same panelTypeId.
        const withInvBat = { ...t, inverters, batteries };
        return setExistingOnInverterPanels(withInvBat, panelOwnerInverterIds, true);
      });
    }
    setSelectedNodeId(boundaryNode.id);
  };

  const rotateAll = (rotation: 0 | 90 | 180 | 270) => {
    pushHistory();
    const ids = new Set(selectedNodes.map((n) => n.id));
    setNodes((prev) =>
      prev.map((n) =>
        ids.has(n.id) ? { ...n, data: { ...n.data, rotation } } : n
      )
    );
  };

  // No confirmation — Cmd/Ctrl+Z reverses the entire bulk delete in
  // one shot. The pre-undo dialog had become friction without safety
  // benefit since history is always pushed before mutation.
  //
  // Use updateTopology (NOT applyTopologyAndRelayout) for the
  // topology cleanup. The latter clears the history stack as a
  // "structural change ≈ new document" — fine for preset switches
  // but it would wipe the undo snapshot we just pushed, breaking
  // Cmd/Ctrl+Z. The nodes/edges are already filtered in setNodes/
  // setEdges above, so no relayout is needed; we just need topology
  // to reflect the deletion (so the title-block BOM doesn't list
  // ghost inverters/batteries).
  const deleteAll = () => {
    const ids = new Set(selectedNodes.map((n) => n.id));
    pushHistory();
    setNodes((prev) => prev.filter((n) => !ids.has(n.id)));
    setEdges((prev) => prev.filter((e) => !ids.has(e.source) && !ids.has(e.target)));
    if (inverterIds.size || batteryIds.size) {
      // Only drop a topology entry when NO surviving node still
      // references it. Without this, deleting a pasted copy would
      // also wipe the topology entry the original node still
      // depends on (the paste handler now duplicates with fresh
      // ids; this is the safety net for any other path that
      // might leave duplicate refs).
      updateTopology((t) => {
        const survivors = useEditorStore.getState().nodes.filter((n) => !ids.has(n.id));
        const stillRefdInv = new Set<string>();
        const stillRefdBat = new Set<string>();
        for (const n of survivors) {
          const ref = n.data?.topologyRef;
          if (ref?.kind === 'inverter') stillRefdInv.add(ref.id);
          else if (ref?.kind === 'battery') stillRefdBat.add(ref.id);
        }
        const droppedInv = new Set<string>();
        for (const id of inverterIds) {
          if (!stillRefdInv.has(id)) droppedInv.add(id);
        }
        return {
          ...t,
          inverters: t.inverters.filter((i) => !droppedInv.has(i.id)),
          batteries: t.batteries.filter(
            (b) =>
              !(batteryIds.has(b.id) && !stillRefdBat.has(b.id)) &&
              // DC batteries with a now-missing host: drop them too.
              !(b.coupling === 'dc' && b.connectedTo && droppedInv.has(b.connectedTo))
          ),
        };
      });
    }
    setSelectedNodeId(null);
  };
  // Quiet the unused-import warning — applyTopologyAndRelayout is
  // selected at the top of the component but no longer used inside
  // deleteAll. Kept available in case future bulk operations need
  // a full relayout.
  void applyTopologyAndRelayout;

  return (
    <div className="props-panel">
      <div className="props-header">
        {selectedNodes.length} item{selectedNodes.length !== 1 ? 's' : ''} selected
      </div>
      <div className="props-sub">{summary}</div>

      <div className="props-divider">EXISTING annotation</div>
      <button
        type="button" className="btn-ghost"
        onClick={wrapSelectionAsExisting}
        style={{
          width: '100%', fontSize: 12, padding: '8px 12px', marginBottom: 10,
          background: '#f3eafe', color: '#581c87', borderColor: '#c4b5fd',
          fontWeight: 600,
        }}
        title="Draws a single dashed violet rectangle around all selected items, labelled EXISTING. The rectangle is a regular node — you can drag it to reposition or delete it with Backspace."
      >
        Wrap selection in EXISTING box
      </button>
      <div style={{ fontSize: 10.5, color: '#64748b', marginBottom: 10, lineHeight: 1.45 }}>
        Creates one dashed boundary around the selection. To mark a single
        item instead, click it and use the Status toggle in its editor.
      </div>

      <div className="props-divider">Rotate all</div>
      <div className="rotation-buttons">
        {([0, 90, 180, 270] as const).map((r) => (
          <button
            key={r}
            className="rotation-btn"
            onClick={() => rotateAll(r)}
            title={`Set rotation to ${r}° on all selected symbols`}
          >
            {r}°
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <button className="btn-danger" onClick={deleteAll}>
          Delete {selectedNodes.length} items
        </button>
      </div>

      <div className="props-empty" style={{ marginTop: 16 }}>
        <span className="props-hint">
          Hold Cmd/Ctrl and drag on the canvas to box-select multiple symbols.
          Cmd/Ctrl+C / V to copy / paste the selection.
        </span>
      </div>
    </div>
  );
}

/**
 * ExistingWrapperPanel — RHS shown when the user clicks an EXISTING
 * boundary annotation. The wrapper itself is a regular node (id starts
 * with `existing-boundary-`) and re-uses the panelString symbol for
 * SVG rendering, so we MUST NOT route it to PanelEditor (that would
 * pop up the global topology.panels and look like "the other panels
 * appeared in RHS").
 *
 * The panel surfaces:
 *   - what the wrapper is and how to manipulate it
 *   - a button to remove it (the underlying entities keep their
 *     individual `isExisting` flags — the user can clear those one
 *     by one in the per-symbol editors)
 */
function ExistingWrapperPanel({ nodeId }: { nodeId: string }) {
  const setNodes        = useEditorStore((s) => s.setNodes);
  const pushHistory     = useEditorStore((s) => s.pushHistory);
  const setSelectedNodeId = useEditorStore((s) => s.setSelectedNodeId);
  const updateTopology  = useEditorStore((s) => s.updateTopology);
  const node            = useEditorStore((s) => s.nodes.find((n) => n.id === nodeId));
  const allNodes        = useEditorStore((s) => s.nodes);
  const topology        = useEditorStore((s) => s.topology);

  if (!node) {
    return (
      <div className="props-panel">
        <div className="props-empty">
          <span className="props-hint">EXISTING annotation not found.</span>
        </div>
      </div>
    );
  }

  // Find which topology entities currently sit inside this wrapper's
  // bounding box. Used by the "Clear EXISTING tags inside" action so
  // the user can revert the underlying entities to "new" status while
  // keeping (or removing) the wrapper itself.
  const box = node.data?.customBox ?? { w: 200, h: 100 };
  const wx0 = node.position.x;
  const wy0 = node.position.y;
  const wx1 = wx0 + box.w;
  const wy1 = wy0 + box.h;

  const inverterIdsInside = new Set<string>();
  const batteryIdsInside  = new Set<string>();
  const panelInverterIdsInside = new Set<string>();
  for (const n of allNodes) {
    if (n.id === nodeId) continue;
    if (n.id === '__page_frame__' || n.id === '__title_block__') continue;
    if (n.id.startsWith('existing-boundary-')) continue;
    const w = n.measured?.width  ?? (n.width  ?? 80);
    const h = n.measured?.height ?? (n.height ?? 40);
    const cx = n.position.x + w / 2;
    const cy = n.position.y + h / 2;
    if (cx < wx0 || cx > wx1 || cy < wy0 || cy > wy1) continue;
    const ref = n.data?.topologyRef;
    if (ref?.kind === 'inverter' && n.data?.symbolId !== 'panelString') {
      inverterIdsInside.add(ref.id);
    } else if (ref?.kind === 'battery') {
      batteryIdsInside.add(ref.id);
    } else if (n.data?.symbolId === 'panelString' && ref?.kind === 'inverter') {
      panelInverterIdsInside.add(ref.id);
    }
  }

  const insideCount =
    inverterIdsInside.size + batteryIdsInside.size + panelInverterIdsInside.size;

  const summaryParts: string[] = [];
  if (inverterIdsInside.size) summaryParts.push(`${inverterIdsInside.size} inverter${inverterIdsInside.size > 1 ? 's' : ''}`);
  if (batteryIdsInside.size)  summaryParts.push(`${batteryIdsInside.size} batter${batteryIdsInside.size > 1 ? 'ies' : 'y'}`);
  if (panelInverterIdsInside.size) summaryParts.push(`panels on ${panelInverterIdsInside.size} inverter${panelInverterIdsInside.size > 1 ? 's' : ''}`);
  const summary = summaryParts.length ? summaryParts.join(', ') : 'nothing inside the box';

  const deleteWrapper = () => {
    pushHistory();
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setSelectedNodeId(null);
  };

  const clearAllExistingInside = () => {
    if (insideCount === 0) {
      deleteWrapper();
      return;
    }
    pushHistory();
    updateTopology((t) => {
      const inverters = t.inverters.map((i) =>
        inverterIdsInside.has(i.id) ? { ...i, isExisting: false } : i
      );
      const batteries = t.batteries.map((b) =>
        batteryIdsInside.has(b.id) ? { ...b, isExisting: false } : b
      );
      // Panels: route through the shared helper so we only clear the
      // contained inverters' panel types (forking if shared) — without
      // this, OTHER inverters sharing the same panelTypeId would also
      // get their EXISTING tag stripped.
      const withInvBat = { ...t, inverters, batteries };
      return setExistingOnInverterPanels(withInvBat, panelInverterIdsInside, false);
    });
    // The topology now says "not existing", but the React Flow nodes
    // were rendered with `data.isExisting: true` baked in at autoLayout
    // time. Without sweeping that flag off the affected nodes the
    // dashed violet boundary keeps drawing — the title block updates
    // (it reads from topology) but the diagram doesn't, which looks
    // like the action half-worked.
    setNodes((prev) =>
      prev
        // Drop the wrapper annotation itself.
        .filter((n) => n.id !== nodeId)
        // Strip isExisting from any inverter / battery / panel-string
        // node whose topologyRef matches a cleared entity.
        .map((n) => {
          if (!n.data?.isExisting) return n;
          const ref = n.data?.topologyRef;
          const isInverterNode =
            ref?.kind === 'inverter' &&
            n.data?.symbolId !== 'panelString' &&
            inverterIdsInside.has(ref.id);
          const isBatteryNode =
            ref?.kind === 'battery' && batteryIdsInside.has(ref.id);
          const isPanelStringNode =
            n.data?.symbolId === 'panelString' &&
            ref?.kind === 'inverter' &&
            panelInverterIdsInside.has(ref.id);
          if (!isInverterNode && !isBatteryNode && !isPanelStringNode) return n;
          return { ...n, data: { ...n.data, isExisting: false } };
        })
    );
    setSelectedNodeId(null);
  };

  // Eyeball whether topology actually has any existing flags on the
  // contained items — drives whether "Clear" is meaningful.
  const anyTagsToClear =
    topology.inverters.some((i) => inverterIdsInside.has(i.id) && i.isExisting) ||
    topology.batteries.some((b) => batteryIdsInside.has(b.id) && b.isExisting) ||
    !!topology.panelTypes?.some((tp) =>
      tp.isExisting &&
      topology.inverters.some(
        (i) => panelInverterIdsInside.has(i.id) &&
          (i.panelTypeId ?? topology.panelTypes![0]?.id) === tp.id
      )
    );

  return (
    <div className="props-panel">
      <div className="props-header">EXISTING annotation</div>
      <div className="props-sub">
        Dashed boundary marking a section of the diagram as pre-existing equipment.
      </div>

      <div className="props-divider">Contains</div>
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>
        {summary}
      </div>

      <div className="props-divider">Tips</div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14, lineHeight: 1.5 }}>
        Drag the box to reposition. Drag a corner of the underlying canvas to box-select different items, then use <em>Wrap selection in EXISTING box</em> if you want a different group. Backspace also removes this box.
      </div>

      {anyTagsToClear && (
        <button
          type="button" className="btn-ghost"
          onClick={clearAllExistingInside}
          style={{
            width: '100%', fontSize: 12, padding: '8px 12px', marginBottom: 10,
            background: '#f8fafc', color: '#0f172a', borderColor: '#cbd5e1',
            fontWeight: 600,
          }}
          title="Removes the EXISTING flag from each item inside this box and deletes the boundary."
        >
          Clear EXISTING from contained items
        </button>
      )}

      <button className="btn-danger" onClick={deleteWrapper} style={{ width: '100%' }}>
        Remove EXISTING box
      </button>
    </div>
  );
}

/** Default empty-selection view: project info + equipment summary
 *  + a collapsible "shortcuts" reference. The shortcuts panel is
 *  the kind of detail that takes a tool from "good" to "polished" —
 *  it gives new users a way to discover power-user moves without
 *  leaving the editor. */
function DefaultPanel() {
  return (
    <div className="props-panel">
      <LinkedProjectCard />
      <ProjectInfoBody />
      <EquipmentSummary />
      <ShortcutsHint />
    </div>
  );
}

/**
 * "Linked Project" card — shown only when the editor is bound to
 * an SP quote via ?project=<qid>. Surfaces what SP currently has
 * (customer / address / equipment counts + capacities) and a
 * one-click jump back to the SP dashboard so the user can update
 * equipment without losing their place in the SLD.
 *
 * The numbers come from the projectSummary snapshot captured every
 * time we hit project-topology.php. We ALSO compute a live summary
 * from the current SLD topology and compare: if they diverge (the
 * user has added or removed equipment in the editor, OR SP has
 * been updated since the snapshot was taken) we render a warning
 * footer pointing the user at the Rev menu's "↻ From project"
 * action to resolve it.
 */
function LinkedProjectCard() {
  const projectId       = useEditorStore((s) => s.projectId);
  const projectSummary  = useEditorStore((s) => s.projectSummary);
  const topology        = useEditorStore((s) => s.topology);

  // Live summary of the current canvas — useMemo so we don't
  // re-walk the topology on every paint (the topology is stable
  // between edits, and edits commit a new reference).
  const liveSummary = useMemo(() => summariseTopologyLive(topology), [topology]);

  if (!projectId) return null;

  const s = projectSummary;
  const dashboardUrl = `/dashboard.php?quoteid=${encodeURIComponent(projectId)}`;
  const heading = s?.customerName || `Project ${projectId}`;

  const mismatch = s ? findSummaryMismatch(s, liveSummary) : null;

  return (
    <div
      style={{
        margin: '0 0 var(--space-3) 0',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'linear-gradient(180deg, var(--accent-soft) 0%, transparent 100%)',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: 'var(--space-3)' }}>
        <div style={{
          fontSize: 9.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.7,
          color: 'var(--text-faint)',
          marginBottom: 4,
        }}>
          Linked project
        </div>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text)',
          lineHeight: 1.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }} title={heading}>
          {heading}
        </div>
        {s?.address && (
          <div style={{
            fontSize: 11,
            color: 'var(--text-muted)',
            marginTop: 2,
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }} title={s.address}>
            {s.address}
          </div>
        )}

        {s && (
          <div style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginTop: 10,
            padding: '10px 8px 8px 8px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
          }}>
            <CountChip
              label={s.panels === 1 ? 'panel' : 'panels'}
              value={s.panels}
              sub={s.panelKwp > 0 ? `${formatKw(s.panelKwp)} kWp` : undefined}
            />
            <CountChip
              label={s.inverters === 1 ? 'inverter' : 'inverters'}
              value={s.inverters}
              sub={s.inverterKwAc > 0 ? `${formatKw(s.inverterKwAc)} kW AC` : undefined}
            />
            <CountChip
              label={s.batteries === 1 ? 'battery' : 'batteries'}
              value={s.batteries}
              sub={s.batteryKwh > 0 ? `${formatKw(s.batteryKwh)} kWh` : undefined}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <a
            href={dashboardUrl}
            target="_blank"
            rel="noopener"
            className="btn-tertiary"
            style={{
              flex: 1,
              fontSize: 11,
              textAlign: 'center',
              textDecoration: 'none',
              padding: '6px 10px',
            }}
            title="Open this project in the Solar Proof dashboard (new tab)"
          >
            Open in SP →
          </a>
        </div>
      </div>

      {/* Footer warning bar — surfaces drift between the SLD canvas
          and the project snapshot. Tucked into the bottom edge of
          the card so it reads as supplementary info, not an error. */}
      {mismatch && (
        <div
          style={{
            padding: '8px 12px',
            background: 'var(--warning-50)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: 'var(--warning-500)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              marginTop: 1,
            }}
          >
            !
          </span>
          <div style={{ fontSize: 11, color: 'var(--warning-700)', lineHeight: 1.4 }}>
            <strong style={{ fontWeight: 600 }}>Diagram differs from project</strong>
            <div style={{ marginTop: 2, color: 'var(--text-muted)' }}>
              {mismatch}{' '}
              Use <strong style={{ color: 'var(--text-muted)' }}>↻ From project</strong> in the Rev menu to pull the latest.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CountChip({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{
        fontSize: 9.5,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginTop: 3,
      }}>
        {label}
      </div>
      {sub && (
        <div style={{
          fontSize: 9,
          color: 'var(--text-faint)',
          marginTop: 2,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {sub}
        </div>
      )}
    </div>
  );
}

/** Lightweight summary of the live SLD topology, mirroring the
 *  shape that summariseProjectTopology produces server-side. Used
 *  for in-card mismatch detection vs the project snapshot. */
function summariseTopologyLive(t: Topology) {
  const panelTypeById = new Map<string, { pmax: number }>();
  for (const pt of t.panelTypes ?? []) panelTypeById.set(pt.id, pt);
  const legacyPmax = t.panels?.pmax ?? 0;

  let panels = 0;
  let panelKwp = 0;
  let inverterKwAc = 0;
  for (const inv of t.inverters ?? []) {
    inverterKwAc += (inv.capacity ?? 0) / 1000;
    const ptypePmax = (inv.panelTypeId && panelTypeById.get(inv.panelTypeId)?.pmax) || legacyPmax;
    for (const m of inv.mppts ?? []) {
      for (const s of m.strings ?? []) {
        const count = s.panels ?? 0;
        panels    += count;
        panelKwp  += count * ptypePmax;
      }
    }
  }
  let batteryKwh = 0;
  for (const b of t.batteries ?? []) batteryKwh += b.capacity ?? 0;

  return {
    panels,
    panelKwp:     panelKwp / 1000,
    inverters:    (t.inverters ?? []).length,
    inverterKwAc,
    batteries:    (t.batteries ?? []).length,
    batteryKwh,
  };
}

/**
 * Compare project snapshot vs live SLD summary. Returns a short
 * human-readable diff string when they differ, or null when in
 * sync. Whole-number counts are compared exactly; capacities use
 * a small epsilon so floating-point quirks don't trigger false
 * positives.
 */
function findSummaryMismatch(
  proj: NonNullable<ReturnType<typeof useEditorStore.getState>['projectSummary']>,
  live: ReturnType<typeof summariseTopologyLive>,
): string | null {
  const diffs: string[] = [];
  const EPS = 0.05; // 50 W / 50 Wh — finer than a single panel.

  if (proj.panels !== live.panels) {
    diffs.push(`${live.panels} panel${live.panels === 1 ? '' : 's'} here vs ${proj.panels} in project`);
  } else if (Math.abs(proj.panelKwp - live.panelKwp) > EPS) {
    // Same count but different kWp → different panel model picked
    // up locally. Worth flagging.
    diffs.push(`${formatKw(live.panelKwp)} kWp here vs ${formatKw(proj.panelKwp)} kWp in project`);
  }

  if (proj.inverters !== live.inverters) {
    diffs.push(`${live.inverters} inverter${live.inverters === 1 ? '' : 's'} here vs ${proj.inverters} in project`);
  } else if (Math.abs(proj.inverterKwAc - live.inverterKwAc) > EPS) {
    diffs.push(`${formatKw(live.inverterKwAc)} kW AC here vs ${formatKw(proj.inverterKwAc)} kW AC in project`);
  }

  if (proj.batteries !== live.batteries) {
    diffs.push(`${live.batteries} batter${live.batteries === 1 ? 'y' : 'ies'} here vs ${proj.batteries} in project`);
  } else if (Math.abs(proj.batteryKwh - live.batteryKwh) > EPS) {
    diffs.push(`${formatKw(live.batteryKwh)} kWh here vs ${formatKw(proj.batteryKwh)} kWh in project`);
  }

  if (diffs.length === 0) return null;
  // First difference is the headline; if multiple, append count.
  const more = diffs.length > 1 ? ` (+${diffs.length - 1} more)` : '';
  return `${diffs[0]}${more}.`;
}

function formatKw(v: number): string {
  // 0.1 precision below 10, 0 above — readable at a glance.
  if (v < 10) return v.toFixed(1).replace(/\.0$/, '');
  return Math.round(v).toString();
}

function ShortcutsHint() {
  // Cmd vs Ctrl — show the right modifier per platform so the hint
  // is accurate on whatever the user is reading from.
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPad|iPhone/.test(navigator.platform || '');
  const mod = isMac ? '⌘' : 'Ctrl';
  return (
    <details className="props-section">
      <summary>Keyboard shortcuts</summary>
      <div className="props-section-body">
        <ShortcutRow keys={`${mod} K`} desc="Open command palette" />
        <ShortcutRow keys={`${mod} Z`} desc="Undo" />
        <ShortcutRow keys={`${mod} ⇧ Z`} desc="Redo" />
        <ShortcutRow keys={`${mod} A`} desc="Select all" />
        <ShortcutRow keys={`${mod} C / V`} desc="Copy / paste selection" />
        <ShortcutRow keys={`${mod} drag`} desc="Box-select" />
        <ShortcutRow keys="⌫ / Del" desc="Delete selection" />
        <ShortcutRow keys="dbl-click" desc="Edit symbol label" />
      </div>
    </details>
  );
}

function ShortcutRow({ keys, desc }: { keys: string; desc: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '4px 0',
        fontSize: 'var(--fs-sm)',
        color: 'var(--text-muted)',
      }}
    >
      <span>{desc}</span>
      <span className="kbd">{keys}</span>
    </div>
  );
}

/** Title-block click → just project info, no redundant equipment list. */
function ProjectInfoForm() {
  return (
    <div className="props-panel">
      <ProjectInfoBody />
    </div>
  );
}

/** Project info as a body fragment (no .props-panel wrapper). */
function ProjectInfoBody() {
  const project       = useEditorStore((s) => s.topology.project);
  const updateProject = useEditorStore((s) => s.updateProject);
  const pushHistory   = useEditorStore((s) => s.pushHistory);

  const set = (mut: (p: typeof project) => typeof project) => updateProject(mut);

  return (
    <>
      <div className="props-header">Project &amp; Installer</div>
      <div className="props-sub">Drives the title block at the bottom of the page.</div>

      <div className="props-row">
        <label>Installation Address</label>
        <input
          type="text" value={project.address ?? ''}
          onChange={(e) => set((p) => ({ ...p, address: e.target.value }))}
          onFocus={pushHistory}
          placeholder="123 Example St, Suburb, STATE, 0000"
        />
      </div>
      <div className="props-row">
        <label>Installation Date</label>
        <input
          type="date" value={project.installDate ?? ''}
          onChange={(e) => set((p) => ({ ...p, installDate: e.target.value }))}
          onFocus={pushHistory}
        />
      </div>

      <div className="props-divider">Installer</div>
      <div className="props-row">
        <label>Company Name</label>
        <input
          type="text" value={project.installer?.name ?? ''}
          onChange={(e) => set((p) => ({ ...p, installer: { ...p.installer, name: e.target.value } }))}
          onFocus={pushHistory}
          placeholder="Your Solar Co."
        />
      </div>
      <div className="props-row">
        <label>Address</label>
        <input
          type="text" value={project.installer?.address ?? ''}
          onChange={(e) => set((p) => ({ ...p, installer: { ...p.installer, address: e.target.value } }))}
          onFocus={pushHistory}
          placeholder="Office street address"
        />
      </div>
      <div className="props-row">
        <label>Phone</label>
        <input
          type="tel" value={project.installer?.phone ?? ''}
          onChange={(e) => set((p) => ({ ...p, installer: { ...p.installer, phone: e.target.value } }))}
          onFocus={pushHistory}
          placeholder="1300 000 000"
        />
      </div>
      <div className="props-row">
        <label>Logo</label>
        <LogoUpload
          current={project.installer?.logoUrl}
          onPick={(dataUrl) => {
            pushHistory();
            set((p) => ({ ...p, installer: { ...p.installer, logoUrl: dataUrl } }));
          }}
          onClear={() => {
            pushHistory();
            set((p) => ({ ...p, installer: { ...p.installer, logoUrl: undefined } }));
          }}
        />
      </div>

      <div className="props-divider">Drawing</div>
      <div className="props-row">
        <label>Drawing #</label>
        <input
          type="text" value={project.drawingNumber ?? ''}
          onChange={(e) => set((p) => ({ ...p, drawingNumber: e.target.value }))}
          onFocus={pushHistory}
          placeholder="SLD-001"
        />
      </div>
      <div className="props-row">
        <label>Revision</label>
        <input
          type="text" value={project.revision ?? ''}
          onChange={(e) => set((p) => ({ ...p, revision: e.target.value }))}
          onFocus={pushHistory}
          placeholder="A" maxLength={6}
        />
      </div>
    </>
  );
}

// ============================================================
// EQUIPMENT SUMMARY (compact cards in DefaultPanel)
// ============================================================

function EquipmentSummary() {
  const panels    = useEditorStore((s) => s.topology.panels);
  const inverters = useEditorStore((s) => s.topology.inverters);
  const batteries = useEditorStore((s) => s.topology.batteries);
  const nodes     = useEditorStore((s) => s.nodes);
  const setSelectedNodeId = useEditorStore((s) => s.setSelectedNodeId);

  const focusInverter = (invId: string) => {
    const n = nodes.find(
      (x) => x.data?.topologyRef?.kind === 'inverter' && x.data?.topologyRef?.id === invId
    );
    if (n) setSelectedNodeId(n.id);
  };
  const focusBattery = (batId: string) => {
    const n = nodes.find(
      (x) => x.data?.topologyRef?.kind === 'battery' && x.data?.topologyRef?.id === batId
    );
    if (n) setSelectedNodeId(n.id);
  };
  const focusPanel = () => {
    const n = nodes.find((x) => x.data?.symbolId === 'panelString');
    if (n) setSelectedNodeId(n.id);
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 8px', fontSize: 11,
    border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc',
    cursor: 'pointer', marginBottom: 4,
  };

  return (
    <>
      <div className="props-divider">Equipment</div>

      <div onClick={focusPanel} style={rowStyle} title="Click to edit panel model">
        <BrandBadge manufacturer={panels.manufacturer} size={26} kind="panels" />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#64748b', fontSize: 10 }}>PANELS</div>
          <div style={{ color: '#0f172a', fontWeight: 600 }}>
            {panels.manufacturer} {panels.model}
          </div>
          <div style={{ color: '#64748b' }}>{panels.pmax}W</div>
        </div>
        <div style={{ color: '#94a3b8' }}>›</div>
      </div>

      <div style={{ marginBottom: 8 }}>
        {inverters.map((inv) => (
          <div key={inv.id} onClick={() => focusInverter(inv.id)} style={rowStyle} title="Click to edit inverter">
            <BrandBadge manufacturer={inv.manufacturer} size={26} kind="inverters" />
            <div style={{ flex: 1 }}>
              <div style={{ color: '#64748b', fontSize: 10 }}>INVERTER · {inv.id}</div>
              <div style={{ color: '#0f172a', fontWeight: 600 }}>
                {inv.manufacturer} {inv.model}
              </div>
              <div style={{ color: '#64748b' }}>
                {(inv.capacity / 1000).toFixed(1)} kW · {inv.phases}φ · {inv.mppts.length} MPPT
              </div>
            </div>
            <div style={{ color: '#94a3b8' }}>›</div>
          </div>
        ))}
        <AddInverterButton />
      </div>

      <div style={{ marginBottom: 8 }}>
        {batteries.map((bat) => (
          <div key={bat.id} onClick={() => focusBattery(bat.id)} style={rowStyle} title="Click to edit battery">
            <BrandBadge manufacturer={bat.manufacturer} size={26} kind="batteries" />
            <div style={{ flex: 1 }}>
              <div style={{ color: '#64748b', fontSize: 10 }}>BATTERY · {bat.id}</div>
              <div style={{ color: '#0f172a', fontWeight: 600 }}>
                {bat.manufacturer} {bat.model}
              </div>
              <div style={{ color: '#64748b' }}>
                {bat.capacity} kWh · {bat.coupling.toUpperCase()}-coupled
              </div>
            </div>
            <div style={{ color: '#94a3b8' }}>›</div>
          </div>
        ))}
        <AddBatteryButton />
      </div>

      <SubBoardConfig />
      <SurgeProtectionConfig />
    </>
  );
}

function AddInverterButton() {
  const applyTopologyAndRelayout = useEditorStore((s) => s.applyTopologyAndRelayout);
  const [picking, setPicking] = useState(false);
  const [filter,  setFilter]  = useState('');

  const matches = filter
    ? INVERTER_CATALOG.filter((p) =>
        `${p.manufacturer} ${p.model} ${p.note ?? ''}`.toLowerCase().includes(filter.toLowerCase())
      )
    : INVERTER_CATALOG;

  const add = (entry: InverterCatalogEntry) => {
    applyTopologyAndRelayout((t) => {
      const used = new Set(t.inverters.map((i) => i.id));
      let id = `inv${t.inverters.length + 1}`;
      let n = t.inverters.length + 2;
      while (used.has(id)) { id = `inv${n}`; n++; }
      return { ...t, inverters: [...t.inverters, inverterFromCatalog(entry, { id })] };
    });
    setPicking(false); setFilter('');
  };

  if (!picking) {
    return (
      <button type="button" className="btn-ghost" onClick={() => setPicking(true)} style={{ fontSize: 11, padding: '4px 10px' }}>
        + Add inverter
      </button>
    );
  }
  return (
    <CatalogPicker
      placeholder="Search inverters…"
      filter={filter}
      onFilter={setFilter}
      onCancel={() => { setPicking(false); setFilter(''); }}
      kind="inverters"
      items={matches.map((m, i) => ({
        key: `${m.manufacturer}|${m.model}|${i}`,
        manufacturer: m.manufacturer,
        model: m.model,
        sub: `${(m.capacity / 1000).toFixed(1)} kW · ${m.phases}φ · ${m.mpptCount} MPPT · ${m.type}${m.note ? ' · ' + m.note : ''}`,
        onPick: () => add(m),
      }))}
      onCustomInverter={(entry) => add(entry)}
    />
  );
}

function AddBatteryButton() {
  const inverters = useEditorStore((s) => s.topology.inverters);
  const applyTopologyAndRelayout = useEditorStore((s) => s.applyTopologyAndRelayout);
  const [picking, setPicking] = useState(false);
  const [filter,  setFilter]  = useState('');
  const [pickedDc, setPickedDc] = useState<BatteryCatalogEntry | null>(null);
  const hybrids = inverters.filter((i) => i.type === 'hybrid');

  const matches = filter
    ? BATTERY_CATALOG.filter((b) =>
        `${b.manufacturer} ${b.model} ${b.note ?? ''}`.toLowerCase().includes(filter.toLowerCase())
      )
    : BATTERY_CATALOG;

  const add = (entry: BatteryCatalogEntry, connectedTo?: string) => {
    applyTopologyAndRelayout((t) => {
      const used = new Set(t.batteries.map((b) => b.id));
      let id = `bat${t.batteries.length + 1}`;
      let n = t.batteries.length + 2;
      while (used.has(id)) { id = `bat${n}`; n++; }
      return { ...t, batteries: [...t.batteries, batteryFromCatalog(entry, { id, connectedTo })] };
    });
    setPicking(false); setFilter(''); setPickedDc(null);
  };

  const handlePick = (entry: BatteryCatalogEntry) => {
    if (entry.coupling === 'dc') {
      if (hybrids.length === 0) {
        void useEditorStore.getState().askConfirm({
          title: 'Hybrid inverter required',
          message:
            'DC-coupled batteries connect directly to the DC bus of a hybrid PV inverter. Add a hybrid inverter to the diagram first, then try again.',
          singleButton: true,
          tone: 'info',
        });
        return;
      }
      if (hybrids.length === 1) add(entry, hybrids[0].id);
      else setPickedDc(entry);
    } else add(entry);
  };

  if (!picking) {
    return (
      <button type="button" className="btn-ghost" onClick={() => setPicking(true)} style={{ fontSize: 11, padding: '4px 10px' }}>
        + Add battery
      </button>
    );
  }
  if (pickedDc) {
    return (
      <div style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 8, background: '#fff' }}>
        <div style={{ fontSize: 11, marginBottom: 6 }}>
          Attach <strong>{pickedDc.manufacturer} {pickedDc.model}</strong> to which hybrid inverter?
        </div>
        {hybrids.map((h) => (
          <button
            key={h.id} type="button" onClick={() => add(pickedDc, h.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 8px', fontSize: 11, marginBottom: 4,
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer',
            }}
          >
            <strong>{h.manufacturer} {h.model}</strong> ({h.id})
          </button>
        ))}
        <button type="button" className="btn-ghost" onClick={() => setPickedDc(null)} style={{ fontSize: 11, padding: '4px 10px' }}>
          Back
        </button>
      </div>
    );
  }
  return (
    <CatalogPicker
      placeholder="Search batteries…"
      filter={filter}
      onFilter={setFilter}
      onCancel={() => { setPicking(false); setFilter(''); }}
      kind="batteries"
      items={matches.map((m, i) => {
        const dcDisabled = m.coupling === 'dc' && hybrids.length === 0;
        return {
          key: `${m.manufacturer}|${m.model}|${i}`,
          manufacturer: m.manufacturer,
          model: m.model,
          sub: `${m.capacity} kWh · ${m.coupling.toUpperCase()}-coupled${m.note ? ' · ' + m.note : ''}`,
          disabled: dcDisabled,
          disabledReason: dcDisabled ? 'Needs a hybrid PV inverter' : undefined,
          onPick: () => handlePick(m),
        };
      })}
    />
  );
}

// ============================================================
// EQUIPMENT EDITORS (per-symbol)
// ============================================================

function InverterEditor({ invId }: { invId: string }) {
  const inverter = useEditorStore((s) => s.topology.inverters.find((i) => i.id === invId));
  const applyTopologyAndRelayout = useEditorStore((s) => s.applyTopologyAndRelayout);
  const setSelectedNodeId = useEditorStore((s) => s.setSelectedNodeId);
  const pushHistory = useEditorStore((s) => s.pushHistory);

  const [picking, setPicking] = useState(false);
  const [filter,  setFilter]  = useState('');

  if (!inverter) {
    // Canvas node references an inverter id that no longer exists in
    // topology (paste/undo edge-cases, hand-edited JSON, etc). Rather
    // than dead-end the user with "not found", offer the catalog
    // picker inline so they can attach a model — the picked entry is
    // inserted into topology with this exact id, restoring the link.
    return <MissingInverterPicker invId={invId} />;
  }

  const matches = filter
    ? INVERTER_CATALOG.filter((p) =>
        `${p.manufacturer} ${p.model} ${p.note ?? ''}`.toLowerCase().includes(filter.toLowerCase())
      )
    : INVERTER_CATALOG;

  const swap = (entry: InverterCatalogEntry) => {
    pushHistory();
    // Update topology entry. Preserve the inverter's existing
    // panelTypeId + isExisting flag so we don't accidentally reset
    // those when swapping models.
    const updateTopologyAction = useEditorStore.getState().updateTopology;
    updateTopologyAction((t) => ({
      ...t,
      inverters: t.inverters.map((i) =>
        i.id === invId
          ? {
              ...inverterFromCatalog(entry, { id: invId }),
              panelTypeId: i.panelTypeId,
              isExisting: i.isExisting,
            }
          : i
      ),
    }));
    // Patch the corresponding canvas node's label/sublabel in place
    // so the visual updates without re-running autoLayout (which
    // would re-position any hand-placed nodes). If the user wants a
    // full re-layout they can use the wizard.
    const setNodes = useEditorStore.getState().setNodes;
    setNodes((prev) =>
      prev.map((n) => {
        if (n.data?.topologyRef?.kind !== 'inverter') return n;
        if (n.data.topologyRef.id !== invId) return n;
        return {
          ...n,
          data: {
            ...n.data,
            label: entry.model,
            sublabel: `${(entry.capacity / 1000).toFixed(1)} kW · ${entry.phases}φ`,
          },
        };
      })
    );
    setPicking(false); setFilter('');
  };

  const removeMe = () => {
    pushHistory();
    // Drop the topology entry + the canvas node + any wires touching
    // it. We use updateTopology so the history snapshot we just
    // pushed survives (applyTopologyAndRelayout would clear history,
    // breaking Cmd/Ctrl+Z reversal of this delete).
    const setNodes = useEditorStore.getState().setNodes;
    const setEdges = useEditorStore.getState().setEdges;
    const updateTopology = useEditorStore.getState().updateTopology;
    updateTopology((t) => ({
      ...t,
      inverters: t.inverters.filter((i) => i.id !== invId),
      batteries: t.batteries.filter((b) => !(b.coupling === 'dc' && b.connectedTo === invId)),
    }));
    setNodes((prev) => prev.filter((n) => {
      // Drop the inverter's own node + any DC battery node connected
      // to this inverter. Other free-form blocks stay in place.
      const ref = n.data?.topologyRef;
      if (ref?.kind === 'inverter' && ref.id === invId) return false;
      return true;
    }));
    setEdges((prev) => {
      // Drop wires touching the inverter's canvas node id. We need
      // the node id, not the topology id, so look it up in the
      // pre-filter snapshot.
      const invNodeIds = new Set<string>();
      const all = useEditorStore.getState().nodes;
      for (const n of all) {
        const ref = n.data?.topologyRef;
        if (ref?.kind === 'inverter' && ref.id === invId) invNodeIds.add(n.id);
      }
      return prev.filter((e) => !invNodeIds.has(e.source) && !invNodeIds.has(e.target));
    });
    setSelectedNodeId(null);
  };
  // Suppress unused-warning — applyTopologyAndRelayout is still
  // imported at the top of InverterEditor for symmetry with the
  // other editors but no longer needed for plain deletes.
  void applyTopologyAndRelayout;

  const sub = `${(inverter.capacity / 1000).toFixed(1)} kW · ${inverter.phases}φ · ${inverter.mppts.length} MPPT · ${inverter.type}`;

  return (
    <div className="props-panel">
      <div className="props-header">Inverter · {inverter.id}</div>

      <ModelCard
        manufacturer={inverter.manufacturer}
        model={inverter.model}
        sub={sub}
        kind="inverters"
        onOpenPicker={() => setPicking(true)}
        picker={picking ? (
          <CatalogPicker
            placeholder="Search inverters…"
            filter={filter}
            onFilter={setFilter}
            onCancel={() => { setPicking(false); setFilter(''); }}
            kind="inverters"
            items={matches.map((m, i) => ({
              key: `${m.manufacturer}|${m.model}|${i}`,
              manufacturer: m.manufacturer,
              model: m.model,
              sub: `${(m.capacity / 1000).toFixed(1)} kW · ${m.phases}φ · ${m.mpptCount} MPPT · ${m.type}${m.note ? ' · ' + m.note : ''}`,
              onPick: () => swap(m),
            }))}
            onCustomInverter={(entry) => swap(entry)}
          />
        ) : null}
      />

      <div className="props-divider">Specs</div>
      <div className="props-row">
        <label
          title="Hybrid = has a battery port (DC-coupled batteries attach here). String = PV only. Products loaded from a Solar Proof quote are classified by model name, so correct it here if the guess is wrong."
        >Type</label>
        <select
          value={inverter.type}
          onChange={(e) => {
            const next = e.target.value as InverterType;
            if (next === inverter.type) return;
            pushHistory();
            applyTopologyAndRelayout((t) => {
              const inverters = t.inverters.map((i) =>
                i.id === invId ? { ...i, type: next } : i
              );
              // Leaving 'hybrid' orphans any DC battery hanging off
              // this inverter: re-home it on another hybrid if there
              // is one, otherwise flip it to AC-coupled so the
              // topology stays drawable.
              const otherHybrid = inverters.find((i) => i.type === 'hybrid' && i.id !== invId);
              const batteries = next === 'hybrid'
                ? t.batteries
                : t.batteries.map((b) => {
                    if (!(b.coupling === 'dc' && b.connectedTo === invId)) return b;
                    if (otherHybrid) return { ...b, connectedTo: otherHybrid.id };
                    const { connectedTo: _drop, ...rest } = b;
                    void _drop;
                    return { ...rest, coupling: 'ac' as const };
                  });
              return { ...t, inverters, batteries };
            });
          }}
        >
          <option value="string">String (PV only)</option>
          <option value="hybrid">Hybrid (battery port)</option>
          <option value="microinverter">Microinverter</option>
        </select>
      </div>
      <div className="props-row">
        <label>Voltage range</label>
        <div className="props-position">
          {inverter.mppts[0]?.voltageRange[0]}–{inverter.mppts[0]?.voltageRange[1]}V
        </div>
      </div>
      <div className="props-row">
        <label>Max current</label>
        <div className="props-position">{inverter.mppts[0]?.maxCurrent}A per MPPT</div>
      </div>

      <div className="props-divider">String layout</div>
      <MpptLayoutEditor invId={invId} />
      <WiredFreeStringsHint invId={invId} />

      <div className="props-divider">Status</div>
      <ExistingToggle
        checked={inverter.isExisting === true}
        onChange={(next) => {
          pushHistory();
          applyTopologyAndRelayout((t) => ({
            ...t,
            inverters: t.inverters.map((i) =>
              i.id === invId ? { ...i, isExisting: next } : i
            ),
          }));
        }}
      />

      <div style={{ marginTop: 16 }}>
        <button className="btn-danger" onClick={removeMe}>Remove inverter</button>
      </div>
    </div>
  );
}

/**
 * Shown when InverterEditor receives an invId that has no matching
 * entry in topology. Renders a catalog picker; the picked entry is
 * inserted with the exact missing id so the canvas node's
 * topologyRef.id starts resolving again.
 */
function MissingInverterPicker({ invId }: { invId: string }) {
  const updateTopology = useEditorStore((s) => s.updateTopology);
  const pushHistory = useEditorStore((s) => s.pushHistory);
  const [filter, setFilter] = useState('');

  const matches = filter
    ? INVERTER_CATALOG.filter((p) =>
        `${p.manufacturer} ${p.model} ${p.note ?? ''}`.toLowerCase().includes(filter.toLowerCase())
      )
    : INVERTER_CATALOG;

  const attach = (entry: InverterCatalogEntry) => {
    pushHistory();
    updateTopology((t) => ({
      ...t,
      inverters: [...t.inverters, inverterFromCatalog(entry, { id: invId })],
    }));
  };

  return (
    <div className="props-panel">
      <div className="props-header">Inverter · {invId}</div>
      <div className="props-sub" style={{ marginBottom: 12 }}>
        No model is attached to this inverter yet. Pick one to wire it up.
      </div>
      <CatalogPicker
        placeholder="Search inverters…"
        filter={filter}
        onFilter={setFilter}
        onCancel={() => { setFilter(''); }}
        kind="inverters"
        items={matches.map((m, i) => ({
          key: `${m.manufacturer}|${m.model}|${i}`,
          manufacturer: m.manufacturer,
          model: m.model,
          sub: `${(m.capacity / 1000).toFixed(1)} kW · ${m.phases}φ · ${m.mpptCount} MPPT · ${m.type}${m.note ? ' · ' + m.note : ''}`,
          onPick: () => attach(m),
        }))}
      />
    </div>
  );
}

/**
 * FreePanelStringEditor — for panel-string blocks dragged from the
 * palette (no inverter context). Reads/writes the per-instance
 * `freeStringConfig` on the node directly. Editing panels/parallels
 * regenerates the composite's customContent / customBox /
 * customTerminals so the visual updates immediately.
 *
 * Why a separate editor from PanelEditor: that one is built around
 * topology.panelTypes + an inverter's MPPT array. Free-form blocks
 * intentionally don't touch topology — they're standalone glyphs
 * that the user places to communicate intent (e.g. an array that
 * isn't connected to an inverter yet, or a marketing-style sketch).
 */
function FreePanelStringEditor({ nodeId }: { nodeId: string }) {
  const setNodes = useEditorStore((s) => s.setNodes);
  const pushHistory = useEditorStore((s) => s.pushHistory);
  const node = useEditorStore((s) => s.nodes.find((n) => n.id === nodeId));
  const topology = useEditorStore((s) => s.topology);

  if (!node || !node.data.freeStringConfig) {
    return (
      <div className="props-panel">
        <div className="props-empty">Panel string not found.</div>
      </div>
    );
  }

  const cfg = node.data.freeStringConfig;
  const panelTypes = (topology.panelTypes && topology.panelTypes.length > 0)
    ? topology.panelTypes
    : (topology.panels ? [{ ...topology.panels, id: 'default' }] : []);
  const activePanel =
    (cfg.panelTypeId && panelTypes.find((p) => p.id === cfg.panelTypeId)) ||
    panelTypes[0];

  // Build the visible label for the composite. Matches wizard-style
  // labels: just the panel model (or "× N strings · Model" for the
  // collapsed-multi case). The "× N per string" + "TOTAL N panels"
  // badges are drawn internally by renderPanelString — including
  // counts in the label too would render the same number twice.
  const buildLabel = (_panels: number, parallels: number) => {
    const modelPart = activePanel ? activePanel.model : 'Panel string';
    return parallels > 1
      ? `× ${parallels} strings · ${modelPart}`
      : modelPart;
  };

  // Apply config change: regenerate the panel-string SVG and
  // terminals, and patch the node's data.
  const applyConfig = (next: { panels: number; parallels: number; panelTypeId?: string }) => {
    pushHistory();
    const label = buildLabel(next.panels, next.parallels);
    const totalPanels = next.panels * next.parallels;
    // Use the per-string panel count for the composite's panel count
    // (autoLayout's renderPanelString takes "panels per string"); we
    // pass `totalPanels` only when parallels > 1 so the inline
    // "TOTAL N panels" badge fires.
    const rendered = renderPanelString(
      next.panels,
      label,
      next.parallels > 1 ? totalPanels : undefined,
      false,
    );
    setNodes((prev) =>
      prev.map((n) => {
        if (n.id !== nodeId) return n;
        return {
          ...n,
          data: {
            ...n.data,
            customContent: rendered.content,
            customBox: rendered.nativeBox,
            customTerminals: {
              pos: { id: 'pos', x: rendered.terminals.pos.x, y: rendered.terminals.pos.y, type: 'dc+' },
              neg: { id: 'neg', x: rendered.terminals.neg.x, y: rendered.terminals.neg.y, type: 'dc-' },
            },
            freeStringConfig: next,
          },
        };
      })
    );
  };

  const setPanels = (panels: number) =>
    applyConfig({ ...cfg, panels: Math.max(1, panels) });
  const setParallels = (parallels: number) =>
    applyConfig({ ...cfg, parallels: Math.max(1, parallels) });

  // Mark this free-form block as pre-existing (retrofit). Free-form
  // panel strings deliberately don't live in topology, so — like the
  // plain symbols (DC isolator, fuse, meter, etc.) — the EXISTING flag
  // is stored directly on the node's data rather than on a topology
  // entity. SldSymbolNode reads data.isExisting to draw the dashed
  // violet boundary + EXISTING badge, so no SVG regeneration is needed.
  const setExisting = (next: boolean) => {
    pushHistory();
    setNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, isExisting: next } } : n
      )
    );
  };

  // Panel-model picker — when the user picks a model that's already
  // in topology.panelTypes, just point the block at it. Otherwise
  // register a new panelType and use that. Skips duplicate
  // registrations on the same {manufacturer, model}.
  const updateTopology = useEditorStore((s) => s.updateTopology);
  const swapModel = (entry: PanelCatalogEntry) => {
    pushHistory();
    let newTypeId = '';
    updateTopology((t) => {
      const types = (t.panelTypes && t.panelTypes.length > 0)
        ? [...t.panelTypes]
        : (t.panels ? [{ ...t.panels, id: 'default' } as PanelTypeShape] : []);
      const existing = types.find(
        (tp) => tp.manufacturer === entry.manufacturer && tp.model === entry.model
      );
      if (existing) {
        newTypeId = existing.id;
      } else {
        const used = new Set(types.map((tp) => tp.id));
        let candidate = `pt${types.length + 1}`;
        let n = types.length + 2;
        while (used.has(candidate)) { candidate = `pt${n}`; n++; }
        types.push({
          id: candidate,
          manufacturer: entry.manufacturer, model: entry.model,
          pmax: entry.pmax, voc: entry.voc, vmp: entry.vmp,
          isc: entry.isc, imp: entry.imp, fuseRating: entry.fuseRating,
        });
        newTypeId = candidate;
      }
      return { ...t, panelTypes: types, panels: types[0] ?? t.panels };
    });
    applyConfig({ ...cfg, panelTypeId: newTypeId });
    setPicking(false);
    setFilter('');
  };

  const [picking, setPicking] = useState(false);
  const [filter, setFilter] = useState('');
  const matches = filter
    ? PANEL_CATALOG.filter((p) =>
        `${p.manufacturer} ${p.model} ${p.note ?? ''}`.toLowerCase().includes(filter.toLowerCase())
      )
    : PANEL_CATALOG;

  const totalPanels = cfg.panels * cfg.parallels;
  const totalKw = activePanel ? (totalPanels * activePanel.pmax) / 1000 : 0;

  // Validation. Free-form blocks aren't tied to a specific inverter,
  // so we validate against (a) the first inverter's first MPPT if
  // there's one in the topology — gives a real spec to check —
  // otherwise (b) AS/NZS 5033 residential PV limit of 600 V on the
  // string side and 30 A per string. Same warning UI as the wizard
  // and MpptLayoutEditor so the experience is consistent.
  const firstInverter = topology.inverters[0];
  const firstMppt = firstInverter?.mppts[0];
  const refMaxVoc = firstMppt?.voltageRange[1] ?? 600;
  const refMinVoc = firstMppt?.voltageRange[0];
  const refMaxCurrent = firstMppt?.maxCurrent ?? 30;
  const refSource = firstInverter
    ? `vs ${firstInverter.manufacturer} ${firstInverter.model} MPPT`
    : 'vs AS/NZS 5033 residential limits';

  const warnings: string[] = [];
  if (activePanel) {
    const stringVoc = activePanel.voc * cfg.panels;
    const totalCurrent = activePanel.isc * cfg.parallels;
    if (stringVoc > refMaxVoc) {
      warnings.push(
        `String Voc ${stringVoc.toFixed(0)}V > ${refMaxVoc}V max — reduce panels per string (${refSource})`
      );
    } else if (refMinVoc && stringVoc < refMinVoc * 0.7) {
      warnings.push(
        `String Voc ${stringVoc.toFixed(0)}V < ${(refMinVoc * 0.7).toFixed(0)}V — add panels per string (${refSource})`
      );
    }
    if (totalCurrent > refMaxCurrent) {
      warnings.push(
        `Combined string current ${totalCurrent.toFixed(1)}A > ${refMaxCurrent}A — reduce parallels (${refSource})`
      );
    }
    if (cfg.panels > 30) {
      warnings.push(
        `${cfg.panels} panels per string is unusually high — typical residential strings are 8–14 panels`
      );
    }
  }

  return (
    <div className="props-panel">
      <div className="props-header">Panel string</div>
      <div className="props-sub" style={{ marginBottom: 8 }}>
        Free-form block — not tied to an inverter MPPT.
      </div>

      {activePanel && (
        <ModelCard
          manufacturer={activePanel.manufacturer}
          model={activePanel.model}
          sub={`${activePanel.pmax}W · Voc ${activePanel.voc}V · Isc ${activePanel.isc}A`}
          kind="panels"
          onOpenPicker={() => setPicking(true)}
          picker={picking ? (
            <CatalogPicker
              placeholder="Search panels…"
              filter={filter}
              onFilter={setFilter}
              onCancel={() => { setPicking(false); setFilter(''); }}
              kind="panels"
              items={matches.map((m, i) => ({
                key: `${m.manufacturer}|${m.model}|${i}`,
                manufacturer: m.manufacturer,
                model: m.model,
                sub: `${m.pmax}W · ${m.voc}V · ${m.isc}A${m.note ? ' · ' + m.note : ''}`,
                onPick: () => swapModel(m),
              }))}
            />
          ) : null}
        />
      )}

      <div className="props-divider">String layout</div>
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Field label="Panels per string" inline>
          <input
            className="input"
            type="number"
            min={1}
            max={40}
            value={cfg.panels}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) setPanels(n);
            }}
            style={{ width: 90 }}
          />
        </Field>
        <Field label="Parallel strings" inline>
          <input
            className="input"
            type="number"
            min={1}
            max={6}
            value={cfg.parallels}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) setParallels(n);
            }}
            style={{ width: 90 }}
          />
        </Field>
      </div>

      <div
        style={{
          marginTop: 'var(--space-3)',
          padding: 'var(--space-3)',
          background: warnings.length > 0 ? 'var(--warning-50)' : 'var(--surface-3)',
          border: `1px solid ${warnings.length > 0 ? 'var(--warning-500)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--fs-base)',
          color: 'var(--text-muted)',
        }}
      >
        <strong style={{ color: 'var(--text)' }}>{totalPanels}</strong> panels total
        {activePanel && (
          <>
            {' · '}
            <strong style={{ color: 'var(--text)' }}>{totalKw.toFixed(2)} kW DC</strong>
            {cfg.panels > 1 && (
              <>
                {' · '}
                {(activePanel.voc * cfg.panels).toFixed(0)}V Voc
              </>
            )}
            {cfg.parallels > 1 && (
              <>
                {' · '}
                {(activePanel.isc * cfg.parallels).toFixed(1)}A Isc
              </>
            )}
          </>
        )}
        {warnings.length > 0 && (
          <ul
            style={{
              margin: '8px 0 0 0',
              paddingLeft: 20,
              color: 'var(--warning-700)',
              fontSize: 'var(--fs-xs)',
            }}
          >
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="props-divider">Status</div>
      <ExistingToggle
        checked={node.data.isExisting === true}
        onChange={setExisting}
      />
    </div>
  );
}

/**
 * Field — small wrapper used here and in the wizard. Local copy so
 * we don't have to export it from NewDiagramConfig.
 */
function Field({
  label, inline, children,
}: {
  label: string;
  inline?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: inline ? 'flex' : 'block',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-muted)',
          marginBottom: inline ? 0 : 4,
        }}
      >
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

/**
 * WiredFreeStringsHint — companion to MpptLayoutEditor.
 *
 * The MPPT layout editor only knows about strings DECLARED on the
 * inverter (topology.inverters[].mppts). Free-form panel-string
 * blocks dragged from the palette and wired up live OUTSIDE that
 * tree. Without this hint, a hand-built diagram could end up with
 * the editor showing one set of strings while the canvas shows a
 * completely different set — confusing.
 *
 * This component walks the wire graph from the inverter's `dc_in`
 * terminal (BFS, single component) and surfaces every connected
 * free-form panel-string block. They're shown read-only with their
 * panels × parallels — the user knows where to click to edit.
 */
function WiredFreeStringsHint({ invId }: { invId: string }) {
  // Subscribe to the store's primitives so Zustand's shallow equality
  // works. Computing the blocks array INSIDE the selector returns a
  // fresh array on each call → infinite re-render loop. We pull
  // stable references out and useMemo to derive blocks.
  const nodes = useEditorStore((s) => s.nodes);
  const edges = useEditorStore((s) => s.edges);
  const topology = useEditorStore((s) => s.topology);

  const blocks = useMemo(() => {
    const invNode = nodes.find(
      (n) => n.data?.topologyRef?.kind === 'inverter' && n.data.topologyRef.id === invId
    );
    if (!invNode) {
      return [] as Array<{ id: string; panels: number; parallels: number; modelName?: string }>;
    }

    // BFS over edges starting from the inverter node, collecting any
    // free-form panel-string blocks reachable (panels can be wired
    // through fuses, isolators, optimisers, etc., so we don't insist
    // on a direct connection).
    const visitedNodes = new Set<string>([invNode.id]);
    const queue: string[] = [invNode.id];
    const found: Array<{ id: string; panels: number; parallels: number; modelName?: string }> = [];
    const panelTypes = (topology.panelTypes && topology.panelTypes.length > 0)
      ? topology.panelTypes
      : (topology.panels ? [{ ...topology.panels, id: 'default' }] : []);

    while (queue.length) {
      const cur = queue.shift()!;
      for (const e of edges) {
        if (e.source !== cur && e.target !== cur) continue;
        const next = e.source === cur ? e.target : e.source;
        if (visitedNodes.has(next)) continue;
        visitedNodes.add(next);
        const node = nodes.find((n) => n.id === next);
        if (!node) continue;
        // Stop at OTHER inverters/batteries — don't bleed across the
        // diagram into someone else's chain.
        const ref = node.data?.topologyRef;
        if (ref && ref.kind === 'inverter' && ref.id !== invId) continue;
        if (ref?.kind === 'battery') continue;
        // If this is a free-form panel string, record it.
        if (node.data?.symbolId === 'panelString' && node.data.freeStringConfig) {
          const cfg = node.data.freeStringConfig;
          const pt = panelTypes.find((p) => p.id === cfg.panelTypeId) ?? panelTypes[0];
          found.push({
            id: node.id,
            panels: cfg.panels,
            parallels: cfg.parallels,
            modelName: pt?.model,
          });
        }
        queue.push(next);
      }
    }
    return found;
  }, [nodes, edges, topology, invId]);

  if (blocks.length === 0) return null;

  const totalPanels = blocks.reduce((n, b) => n + b.panels * b.parallels, 0);

  return (
    <div
      style={{
        marginTop: 'var(--space-3)',
        padding: 'var(--space-3)',
        background: 'var(--info-50)',
        border: '1px solid var(--info-500)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--fs-sm)',
        color: 'var(--info-700)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Wired free-form blocks
      </div>
      <div style={{ marginBottom: 6, fontSize: 'var(--fs-xs)' }}>
        {blocks.length} free-form panel-string block{blocks.length > 1 ? 's' : ''} on the canvas connect to this inverter ({totalPanels} panels total). They edit independently — click the block to change its panels / parallels / model.
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--fs-xs)' }}>
        {blocks.map((b) => (
          <li key={b.id}>
            {b.parallels > 1 ? `${b.parallels} × ` : ''}
            {b.panels} panel{b.panels > 1 ? 's' : ''}
            {b.modelName ? ` · ${b.modelName}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * MpptLayoutEditor — per-MPPT controls inside the InverterEditor.
 *
 * Mirrors the wizard's "Per-MPPT layout" section so editing an
 * existing diagram feels exactly like setting one up: each MPPT has
 * Strings × Panels-each rows with live string Voc / per-MPPT current
 * derived from the active panel type. Out-of-range values raise a
 * soft warning per row.
 *
 * Adds and removes MPPTs respect the inverter model's catalog
 * mpptCount as a soft cap: the model says 2 MPPTs, the user can
 * have anywhere from 1 to 2. The Add button disables at the cap.
 */
function MpptLayoutEditor({ invId }: { invId: string }) {
  const topology = useEditorStore((s) => s.topology);
  const apply = useEditorStore((s) => s.applyTopologyAndRelayout);
  const pushHistory = useEditorStore((s) => s.pushHistory);

  const inv = topology.inverters.find((i) => i.id === invId);
  if (!inv) return null;

  // Resolve the active panel type for spec-validation messages. Same
  // resolution path as PanelEditor — falls back to legacy panels.
  const panelTypes = topology.panelTypes && topology.panelTypes.length > 0
    ? topology.panelTypes
    : (topology.panels ? [{ ...topology.panels, id: 'default' }] : []);
  const activePanel =
    (inv.panelTypeId && panelTypes.find((p) => p.id === inv.panelTypeId)) ||
    panelTypes[0] ||
    topology.panels;

  // Catalog entry for this inverter — gives us the model-level
  // mpptCount cap for "Add MPPT". When the user has hand-edited an
  // inverter not in the catalog, fall back to "current count + 2"
  // so they can still grow the MPPT array if needed.
  const catalog = INVERTER_CATALOG.find(
    (c) => c.manufacturer === inv.manufacturer && c.model === inv.model
  );
  const mpptCap = catalog?.mpptCount ?? Math.max(inv.mppts.length, 6);
  const canAdd = inv.mppts.length < mpptCap;

  // Update one MPPT's strings count + panels-per-string. We keep the
  // structure UNIFORM across the strings on that MPPT — same as the
  // wizard. Non-uniform setups (rare) get flattened on edit; users
  // who really need mixed strings can split the inverter into two.
  const updateMppt = (
    mpptIdx: number,
    next: { strings: number; panelsPerString: number }
  ) => {
    pushHistory();
    apply((t) => {
      const inverters = t.inverters.map((i) => {
        if (i.id !== invId) return i;
        const mppts = i.mppts.map((m, idx) => {
          if (idx !== mpptIdx) return m;
          const strings = Array.from({ length: Math.max(1, next.strings) }, () => ({
            panels: Math.max(1, next.panelsPerString),
          }));
          return { ...m, strings };
        });
        return { ...i, mppts };
      });
      return { ...t, inverters };
    });
  };

  const removeMppt = (mpptIdx: number) => {
    if (inv.mppts.length <= 1) return; // leave at least 1
    pushHistory();
    apply((t) => {
      const inverters = t.inverters.map((i) => {
        if (i.id !== invId) return i;
        const mppts = i.mppts
          .filter((_, idx) => idx !== mpptIdx)
          // Re-number sequentially so labels stay tidy (MPPT 1, 2, …)
          .map((m, idx) => ({ ...m, mpptNumber: idx + 1 }));
        return { ...i, mppts };
      });
      return { ...t, inverters };
    });
  };

  const addMppt = () => {
    if (!canAdd) return;
    pushHistory();
    apply((t) => {
      const inverters = t.inverters.map((i) => {
        if (i.id !== invId) return i;
        // New MPPT inherits the previous MPPT's spec (current limits,
        // voltage range) and a single 10-panel string as the default.
        const last = i.mppts[i.mppts.length - 1];
        const next = {
          mpptNumber: i.mppts.length + 1,
          maxCurrent: last?.maxCurrent ?? catalog?.maxCurrentPerMppt ?? 15,
          voltageRange: last?.voltageRange ?? catalog?.voltageRange ?? [80, 1000] as [number, number],
          strings: [{ panels: last?.strings[0]?.panels ?? 10 }],
        };
        return { ...i, mppts: [...i.mppts, next] };
      });
      return { ...t, inverters };
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {inv.mppts.length === 0 && (
        <div
          style={{
            padding: 'var(--space-3)',
            border: '1px dashed var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface-3)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-muted)',
            lineHeight: 1.55,
          }}
        >
          <strong style={{ color: 'var(--text)' }}>No MPPTs declared yet.</strong>{' '}
          Two ways to add strings:
          <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
            <li>Click <strong>+ Add MPPT</strong> below to declare strings on the inverter directly.</li>
            <li>Or drag a <strong>Panel String</strong> block from the palette and wire it to this inverter — it'll appear under "Wired free-form blocks" once connected.</li>
          </ol>
        </div>
      )}
      {inv.mppts.map((m, idx) => {
        const stringsCount = m.strings.length || 1;
        const panelsPerString = m.strings[0]?.panels ?? 10;
        const totalPanels = stringsCount * panelsPerString;

        // Live spec checks — same logic as the new-diagram wizard.
        const warnings: string[] = [];
        if (activePanel) {
          const stringVoc = activePanel.voc * panelsPerString;
          const stringCurrent = activePanel.isc * stringsCount;
          if (stringVoc > m.voltageRange[1]) {
            warnings.push(
              `${stringVoc.toFixed(0)}V Voc exceeds MPPT max ${m.voltageRange[1]}V — reduce panels`
            );
          } else if (stringVoc < m.voltageRange[0] * 0.7) {
            warnings.push(
              `${stringVoc.toFixed(0)}V Voc low for MPPT (starts ${m.voltageRange[0]}V) — add panels`
            );
          }
          if (stringCurrent > m.maxCurrent) {
            warnings.push(
              `${stringCurrent.toFixed(1)}A exceeds MPPT limit ${m.maxCurrent}A — reduce strings`
            );
          }
        }
        const hasIssue = warnings.length > 0;

        return (
          <div
            key={idx}
            style={{
              padding: '8px 10px',
              border: `1px solid ${hasIssue ? 'var(--warning-500)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-md)',
              background: hasIssue ? 'var(--warning-50)' : 'var(--surface)',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <div style={{
                fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--text)', minWidth: 60,
              }}>
                MPPT {m.mpptNumber}
              </div>
              <CompactNumberField
                label="Strings"
                value={stringsCount}
                onChange={(v) => updateMppt(idx, { strings: v, panelsPerString })}
                min={1}
                max={6}
              />
              <CompactNumberField
                label="Panels each"
                value={panelsPerString}
                onChange={(v) => updateMppt(idx, { strings: stringsCount, panelsPerString: v })}
                min={1}
                max={40}
              />
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-faint)', marginLeft: 'auto' }}>
                {totalPanels} panels
                {activePanel && (
                  <>
                    {' · '}
                    {(activePanel.voc * panelsPerString).toFixed(0)}V Voc
                    {' · '}
                    {(activePanel.isc * stringsCount).toFixed(1)}A
                  </>
                )}
              </div>
              {inv.mppts.length > 1 && (
                <button
                  type="button"
                  className="btn-tertiary btn-sm"
                  title="Remove this MPPT"
                  onClick={() => removeMppt(idx)}
                  style={{ padding: '2px 8px' }}
                >
                  ✕
                </button>
              )}
            </div>
            {warnings.map((w, i) => (
              <div key={i} style={{ fontSize: 'var(--fs-xs)', color: 'var(--warning-700)' }}>
                ⚠ {w}
              </div>
            ))}
          </div>
        );
      })}

      <button
        type="button"
        className="btn-secondary btn-sm"
        onClick={addMppt}
        disabled={!canAdd}
        title={
          canAdd
            ? `Add another MPPT (model supports up to ${mpptCap})`
            : `Inverter model is at its ${mpptCap}-MPPT capacity`
        }
        style={{ alignSelf: 'flex-start', marginTop: 4 }}
      >
        + Add MPPT
      </button>
    </div>
  );
}

/**
 * CompactNumberField — narrow inline label + number input. Used in
 * MpptLayoutEditor so each MPPT row stays on one line at 300px width.
 */
function CompactNumberField({
  label, value, onChange, min, max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>{label}</span>
      <input
        className="input"
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        style={{ width: 64, padding: '4px 8px' }}
      />
    </label>
  );
}

function BatteryEditor({ batId }: { batId: string }) {
  const battery   = useEditorStore((s) => s.topology.batteries.find((b) => b.id === batId));
  const inverters = useEditorStore((s) => s.topology.inverters);
  const applyTopologyAndRelayout = useEditorStore((s) => s.applyTopologyAndRelayout);
  const updateTopology = useEditorStore((s) => s.updateTopology);
  const setSelectedNodeId = useEditorStore((s) => s.setSelectedNodeId);
  const pushHistory = useEditorStore((s) => s.pushHistory);

  const [picking, setPicking] = useState(false);
  const [filter,  setFilter]  = useState('');

  if (!battery) {
    // Same recovery path as InverterEditor: surface the catalog picker
    // so the user can attach a model rather than getting stuck on a
    // dead-end "not found" message.
    return <MissingBatteryPicker batId={batId} />;
  }

  const hybrids = inverters.filter((i) => i.type === 'hybrid');
  const matches = filter
    ? BATTERY_CATALOG.filter((b) =>
        `${b.manufacturer} ${b.model} ${b.note ?? ''}`.toLowerCase().includes(filter.toLowerCase())
      )
    : BATTERY_CATALOG;

  const swap = (entry: BatteryCatalogEntry) => {
    if (entry.coupling === 'dc' && hybrids.length === 0) {
      void useEditorStore.getState().askConfirm({
        title: 'Hybrid inverter required',
        message:
          'DC-coupled batteries connect directly to the DC bus of a hybrid PV inverter. Add a hybrid inverter to the diagram first, then try again.',
        singleButton: true,
        tone: 'info',
      });
      return;
    }
    const connectedTo = entry.coupling === 'dc'
      ? (battery.connectedTo && hybrids.some((h) => h.id === battery.connectedTo))
          ? battery.connectedTo
          : hybrids[0]?.id
      : undefined;
    applyTopologyAndRelayout((t) => ({
      ...t,
      batteries: t.batteries.map((b) =>
        b.id === batId ? batteryFromCatalog(entry, { id: batId, connectedTo }) : b
      ),
    }));
    setPicking(false); setFilter('');
  };

  const removeMe = () => {
    pushHistory();
    // Drop the topology entry + the canvas node + any wires touching
    // it via updateTopology so the just-pushed history snapshot
    // survives (applyTopologyAndRelayout would clear the stack).
    const setNodes = useEditorStore.getState().setNodes;
    const setEdges = useEditorStore.getState().setEdges;
    const updateTopology = useEditorStore.getState().updateTopology;
    updateTopology((t) => ({
      ...t,
      batteries: t.batteries.filter((b) => b.id !== batId),
    }));
    const battNodeIds = new Set<string>();
    const all = useEditorStore.getState().nodes;
    for (const n of all) {
      const ref = n.data?.topologyRef;
      if (ref?.kind === 'battery' && ref.id === batId) battNodeIds.add(n.id);
    }
    setNodes((prev) => prev.filter((n) => !battNodeIds.has(n.id)));
    setEdges((prev) => prev.filter((e) => !battNodeIds.has(e.source) && !battNodeIds.has(e.target)));
    setSelectedNodeId(null);
  };
  void applyTopologyAndRelayout;

  const sub = `${battery.capacity} kWh · ${battery.coupling.toUpperCase()}-coupled${battery.backupGateway ? ' · backup' : ''}${battery.coupling === 'dc' && battery.connectedTo ? ' · → ' + battery.connectedTo : ''}`;

  return (
    <div className="props-panel">
      <div className="props-header">Battery · {battery.id}</div>

      <ModelCard
        manufacturer={battery.manufacturer}
        model={battery.model}
        sub={sub}
        kind="batteries"
        onOpenPicker={() => setPicking(true)}
        picker={picking ? (
          <CatalogPicker
            placeholder="Search batteries…"
            filter={filter}
            onFilter={setFilter}
            onCancel={() => { setPicking(false); setFilter(''); }}
            kind="batteries"
            items={matches.map((m, i) => {
              const dcDisabled = m.coupling === 'dc' && hybrids.length === 0;
              return {
                key: `${m.manufacturer}|${m.model}|${i}`,
                manufacturer: m.manufacturer,
                model: m.model,
                sub: `${m.capacity} kWh · ${m.coupling.toUpperCase()}-coupled${m.note ? ' · ' + m.note : ''}`,
                disabled: dcDisabled,
                disabledReason: dcDisabled ? 'Needs a hybrid PV inverter' : undefined,
                onPick: () => swap(m),
              };
            })}
          />
        ) : null}
      />

      <div className="props-divider">Specs</div>
      <div className="props-row">
        <label
          title="Batteries sharing a label form one stack/BMS. Give different labels (e.g. Stack 1, Stack 2) to split modules into separate stacks, each with its own battery inverter. Blank merges identical modules."
        >Stack / BMS</label>
        <input
          type="text"
          value={battery.stackLabel ?? ''}
          onFocus={pushHistory}
          onChange={(e) => {
            const v = e.target.value;
            updateTopology((t) => ({
              ...t,
              batteries: t.batteries.map((b) =>
                b.id === batId ? { ...b, stackLabel: v.trim() ? v : undefined } : b
              ),
            }));
          }}
          // Re-run layout on blur so the canvas splits/merges stacks to
          // match the labels (cheap; only fires when the field loses focus).
          onBlur={() => applyTopologyAndRelayout((t) => ({ ...t }))}
          placeholder="e.g. Stack 1 — blank merges identical"
        />
      </div>
      <div className="props-row">
        <label
          title="DC-coupled = no inverter of its own, plugs into a hybrid inverter's battery port (Sungrow SBR/SBH, BYD HVS/HVM, GoodWe Lynx...). AC-coupled = has its own inverter and sits on the AC bus (Powerwall, Enphase IQ Battery...). Batteries loaded from a Solar Proof quote are classified by model name - correct it here if needed."
        >Coupling</label>
        <select
          value={battery.coupling}
          onChange={(e) => {
            const next = e.target.value as BatteryCoupling;
            if (next === battery.coupling) return;
            if (next === 'dc' && hybrids.length === 0) {
              void useEditorStore.getState().askConfirm({
                title: 'Hybrid inverter required',
                message:
                  'DC-coupled batteries connect to the battery port of a hybrid PV inverter. Set an inverter\'s Type to "Hybrid" (or add one) first, then switch this battery to DC-coupled.',
                singleButton: true,
                tone: 'info',
              });
              return;
            }
            pushHistory();
            applyTopologyAndRelayout((t) => ({
              ...t,
              batteries: t.batteries.map((b) => {
                if (b.id !== batId) return b;
                if (next === 'dc') {
                  const { inverter: _inv, ...rest } = b;
                  void _inv;
                  return { ...rest, coupling: 'dc' as const, connectedTo: hybrids[0].id };
                }
                const { connectedTo: _c, ...rest } = b;
                void _c;
                return { ...rest, coupling: 'ac' as const };
              }),
            }));
          }}
        >
          <option value="ac">AC-coupled (own inverter)</option>
          <option value="dc" disabled={hybrids.length === 0}>
            DC-coupled (via hybrid inverter){hybrids.length === 0 ? ' - needs a hybrid' : ''}
          </option>
        </select>
      </div>
      {battery.coupling === 'dc' && (
        <div className="props-row">
          <label>Attached to</label>
          <select
            value={battery.connectedTo && hybrids.some((h) => h.id === battery.connectedTo) ? battery.connectedTo : ''}
            onChange={(e) => {
              const next = e.target.value;
              if (!next || next === battery.connectedTo) return;
              pushHistory();
              applyTopologyAndRelayout((t) => ({
                ...t,
                batteries: t.batteries.map((b) =>
                  b.id === batId ? { ...b, connectedTo: next } : b
                ),
              }));
            }}
          >
            {!battery.connectedTo || !hybrids.some((h) => h.id === battery.connectedTo) ? (
              <option value="">- pick a hybrid -</option>
            ) : null}
            {hybrids.map((h) => (
              <option key={h.id} value={h.id}>{h.id} · {h.manufacturer} {h.model}</option>
            ))}
          </select>
        </div>
      )}
      {battery.backupGateway && (
        <div className="props-row">
          <label>Backup</label>
          <div className="props-position">Gateway included</div>
        </div>
      )}
      {battery.coupling === 'ac' && battery.inverter && (
        <div className="props-row">
          <label>AC inverter</label>
          <div className="props-position">{(battery.inverter.capacity / 1000).toFixed(1)} kW · {battery.inverter.phases}φ</div>
        </div>
      )}

      <div className="props-divider">Status</div>
      <ExistingToggle
        checked={battery.isExisting === true}
        onChange={(next) => {
          pushHistory();
          applyTopologyAndRelayout((t) => ({
            ...t,
            batteries: t.batteries.map((b) =>
              b.id === batId ? { ...b, isExisting: next } : b
            ),
          }));
        }}
      />

      <div style={{ marginTop: 16 }}>
        <button className="btn-danger" onClick={removeMe}>Remove battery</button>
      </div>
    </div>
  );
}

/**
 * ExistingToggle — shared checkbox UI used in InverterEditor /
 * BatteryEditor / PanelEditor. Toggles isExisting on the topology
 * entity. SldSymbolNode handles the visual: dashed violet boundary +
 * EXISTING badge top-right.
 */
function ExistingToggle({
  checked, onChange,
}: { checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <label
      title="Mark this item as pre-existing (retrofit). Existing equipment is wrapped with a dashed violet boundary on the canvas and tagged in the bottom title block's BOM so installers can immediately distinguish what's already on site from what's being newly added."
      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#0f172a', padding: '4px 0' }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      Mark as <strong>EXISTING</strong> (retrofit)
    </label>
  );
}

/**
 * Mirror of MissingInverterPicker — surfaces the catalog picker when
 * BatteryEditor receives a batId that has no matching topology entry,
 * so users can attach a battery model rather than dead-ending on
 * "Battery not found." The picked entry is inserted with the exact
 * missing id so the canvas node's topologyRef.id starts resolving
 * again.
 */
function MissingBatteryPicker({ batId }: { batId: string }) {
  const updateTopology = useEditorStore((s) => s.updateTopology);
  const pushHistory = useEditorStore((s) => s.pushHistory);
  const inverters = useEditorStore((s) => s.topology.inverters);
  const [filter, setFilter] = useState('');

  // DC-coupled batteries need a hybrid PV inverter; if there's none in
  // the project, only AC-coupled choices are valid. Disabled rows still
  // show but with an explanatory tooltip.
  const hybrids = inverters.filter((i) => i.type === 'hybrid');

  const matches = filter
    ? BATTERY_CATALOG.filter((b) =>
        `${b.manufacturer} ${b.model} ${b.note ?? ''}`.toLowerCase().includes(filter.toLowerCase())
      )
    : BATTERY_CATALOG;

  const attach = (entry: BatteryCatalogEntry) => {
    pushHistory();
    updateTopology((t) => ({
      ...t,
      batteries: [
        ...t.batteries,
        batteryFromCatalog(entry, {
          id: batId,
          // Default DC-coupled batteries to the first hybrid inverter so
          // the topology stays valid. AC-coupled batteries don't need a
          // partner inverter.
          connectedTo: entry.coupling === 'dc' ? hybrids[0]?.id : undefined,
        }),
      ],
    }));
  };

  return (
    <div className="props-panel">
      <div className="props-header">Battery · {batId}</div>
      <div className="props-sub" style={{ marginBottom: 12 }}>
        No model is attached to this battery yet. Pick one to wire it up.
      </div>
      <CatalogPicker
        placeholder="Search batteries…"
        filter={filter}
        onFilter={setFilter}
        onCancel={() => { setFilter(''); }}
        kind="batteries"
        items={matches.map((m, i) => {
          const dcDisabled = m.coupling === 'dc' && hybrids.length === 0;
          return {
            key: `${m.manufacturer}|${m.model}|${i}`,
            manufacturer: m.manufacturer,
            model: m.model,
            sub: `${m.capacity} kWh · ${m.coupling.toUpperCase()}-coupled${m.note ? ' · ' + m.note : ''}`,
            disabled: dcDisabled,
            disabledReason: dcDisabled ? 'Needs a hybrid PV inverter' : undefined,
            onPick: () => attach(m),
          };
        })}
      />
    </div>
  );
}

/**
 * PanelEditor — when a panelString is clicked, edits the panel TYPE used
 * by THAT inverter (not the global topology.panels). This is the
 * multi-panel-type model: each inverter carries `panelTypeId` referencing
 * topology.panelTypes[]. Picking a new model creates/updates the type
 * and points this inverter at it.
 *
 * Backward compat: if `topology.panelTypes` is empty (legacy preset that
 * only has `topology.panels`), we materialise a 'default' type from the
 * legacy field on first edit so existing presets keep rendering.
 */
function PanelEditor({ invId }: { invId?: string }) {
  const topology       = useEditorStore((s) => s.topology);
  // Model swap uses updateTopology + in-place node patching so user-dragged
  // positions stay put. EXISTING-toggle further down still uses
  // applyTopologyAndRelayout because flipping EXISTING re-renders the
  // panel-string composites with a dashed boundary + badge, which is
  // structural enough to warrant a clean relayout of that section.
  const updateTopology = useEditorStore((s) => s.updateTopology);
  const applyTopologyAndRelayout = useEditorStore((s) => s.applyTopologyAndRelayout);
  const pushHistory    = useEditorStore((s) => s.pushHistory);

  const [picking, setPicking] = useState(false);
  const [filter,  setFilter]  = useState('');

  // Resolve the panel TYPE this inverter uses (or the legacy topology.panels
  // when there's no inverter context — clicking a panelString should always
  // give us an inverter id, but we fall back gracefully).
  const inv = invId ? topology.inverters.find((i) => i.id === invId) : undefined;
  const panelTypes = topology.panelTypes && topology.panelTypes.length > 0
    ? topology.panelTypes
    : (topology.panels ? [{ ...topology.panels, id: 'default' }] : []);
  const activePanel =
    (inv?.panelTypeId && panelTypes.find((p) => p.id === inv.panelTypeId)) ||
    panelTypes[0] ||
    topology.panels;

  if (!activePanel) {
    return <div className="props-panel"><div className="props-empty">No panel configured.</div></div>;
  }

  const matches = filter
    ? PANEL_CATALOG.filter((p) =>
        `${p.manufacturer} ${p.model} ${p.note ?? ''}`.toLowerCase().includes(filter.toLowerCase())
      )
    : PANEL_CATALOG;

  /**
   * Apply the picked catalog entry. Strategy:
   *   1. If this inverter is the ONLY consumer of its current panel type,
   *      mutate that type in place (avoids type proliferation).
   *   2. Otherwise create a new type and point this inverter at it.
   *   3. Garbage-collect any panel types no longer referenced by any
   *      inverter (keeps the registry tight).
   *
   * Critically this uses updateTopology (not applyTopologyAndRelayout) so
   * user-dragged node positions stay put. The visual change for a model
   * swap is only the model name appearing in each lane label (e.g.
   * "MPPT 1 · OldModel" → "MPPT 1 · NewModel"), so we do an in-place
   * substitution on the customContent SVG of every panel-string node
   * tied to this inverter rather than triggering a full relayout.
   */
  const swap = (entry: PanelCatalogEntry) => {
    pushHistory();

    // Capture the old model name BEFORE the topology mutation so we can
    // find " · OldModel" inside each panel-string composite's SVG label.
    const oldModel = activePanel.model;

    updateTopology((t) => {
      // Materialise default type from legacy `panels` if missing
      let types = (t.panelTypes && t.panelTypes.length > 0)
        ? [...t.panelTypes]
        : (t.panels ? [{ ...t.panels, id: 'default' } as PanelTypeShape] : []);

      const targetInv = invId ? t.inverters.find((i) => i.id === invId) : undefined;
      const currentTypeId = targetInv?.panelTypeId ?? types[0]?.id;

      // Count how many inverters reference the current type (treating
      // missing panelTypeId as the first/default type).
      const refCount = t.inverters.filter((i) =>
        (i.panelTypeId ?? types[0]?.id) === currentTypeId
      ).length;

      let newTypeId: string;
      if (refCount === 1 && currentTypeId) {
        // Only this inverter uses it — mutate in place.
        types = types.map((tp) => tp.id === currentTypeId
          ? { ...tp,
              manufacturer: entry.manufacturer, model: entry.model,
              pmax: entry.pmax, voc: entry.voc, vmp: entry.vmp,
              isc: entry.isc, imp: entry.imp, fuseRating: entry.fuseRating }
          : tp
        );
        newTypeId = currentTypeId;
      } else {
        // Other inverters share the current type — fork off a new one.
        const used = new Set(types.map((tp) => tp.id));
        let candidate = `pt${types.length + 1}`;
        let n = types.length + 2;
        while (used.has(candidate)) { candidate = `pt${n}`; n++; }
        newTypeId = candidate;
        types.push({
          id: newTypeId,
          manufacturer: entry.manufacturer, model: entry.model,
          pmax: entry.pmax, voc: entry.voc, vmp: entry.vmp,
          isc: entry.isc, imp: entry.imp, fuseRating: entry.fuseRating,
        });
      }

      const inverters = t.inverters.map((i) =>
        i.id === invId ? { ...i, panelTypeId: newTypeId } : i
      );

      // GC: drop any panel types nobody references
      const referenced = new Set(inverters.map((i) => i.panelTypeId ?? types[0]?.id));
      types = types.filter((tp) => referenced.has(tp.id));

      return {
        ...t,
        panelTypes: types,
        // Keep legacy `panels` aligned with the first type for any code
        // path that hasn't switched over yet (BOM aggregation, exports).
        panels: types[0] ?? t.panels,
        inverters,
      };
    });

    // Surgically refresh the lane label inside each panel-string node
    // owned by this inverter. autoLayout writes labels like
    // "MPPT 1 · ModelName" / "× 4 strings · ModelName" — the ` · Model`
    // suffix is the only place the model name appears in the composite
    // SVG, so a targeted text replacement updates the visual without
    // disturbing positions or wiring. Nodes belonging to other inverters
    // (which still reference the old type after a fork) are untouched.
    if (oldModel && oldModel !== entry.model) {
      const setNodes = useEditorStore.getState().setNodes;
      // Escape regex specials in the model name (e.g. "JKM-450M+", which
      // contains "+" — a regex metachar) before building the matcher.
      const oldEsc = oldModel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Match ` · OldModel` where the model name terminates at the closing
      // `</text>` tag of the label element (or whitespace, just in case
      // future label formats add trailing content). The leading ` · `
      // separator is autoLayout's modelSuffix prefix and won't appear in
      // any other place inside the composite SVG.
      const labelRe = new RegExp(` · ${oldEsc}(?=<|\\s|$)`, 'g');
      setNodes((prev) => prev.map((n) => {
        if (n.data?.symbolId !== 'panelString') return n;
        const ref = n.data?.topologyRef;
        // Only touch panel-string nodes tied to THIS inverter. Free-form
        // panel strings (no topologyRef.kind === 'inverter') and strings
        // owned by other inverters are left alone — they keep using
        // whatever panel type they were previously bound to.
        if (invId) {
          if (ref?.kind !== 'inverter' || ref.id !== invId) return n;
        } else {
          // Global edit (no invId) — replace across all panel-string
          // nodes that mention the old model in their label.
          if (typeof n.data?.customContent !== 'string') return n;
        }
        if (typeof n.data?.customContent !== 'string') return n;
        const replaced = n.data.customContent.replace(labelRe, ` · ${entry.model}`);
        if (replaced === n.data.customContent) return n;
        return { ...n, data: { ...n.data, customContent: replaced } };
      }));
    }

    setPicking(false); setFilter('');
  };

  const sub = `${activePanel.pmax}W · Voc ${activePanel.voc}V · Isc ${activePanel.isc}A`;

  // Count how many other inverters share this panel type — if >1, the
  // user gets a soft hint that a swap will fork a new type.
  const sharedCount = inv && activePanel
    ? topology.inverters.filter((i) =>
        (i.panelTypeId ?? panelTypes[0]?.id) === (inv.panelTypeId ?? panelTypes[0]?.id)
      ).length
    : 1;

  return (
    <div className="props-panel">
      <div className="props-header">Panel string</div>
      <div className="props-sub" style={{ marginBottom: 8 }}>
        {inv
          ? <>For inverter <strong>{inv.id}</strong>{sharedCount > 1 && <> (shared with {sharedCount - 1} other{sharedCount > 2 ? 's' : ''} — swap will fork a new type)</>}</>
          : <>Editing the global panel model.</>
        }
      </div>

      <ModelCard
        manufacturer={activePanel.manufacturer}
        model={activePanel.model}
        sub={sub}
        kind="panels"
        onOpenPicker={() => setPicking(true)}
        picker={picking ? (
          <CatalogPicker
            placeholder="Search panels…"
            filter={filter}
            onFilter={setFilter}
            onCancel={() => { setPicking(false); setFilter(''); }}
            kind="panels"
            items={matches.map((m, i) => ({
              key: `${m.manufacturer}|${m.model}|${i}`,
              manufacturer: m.manufacturer,
              model: m.model,
              sub: `${m.pmax}W · ${m.voc}V · ${m.isc}A${m.note ? ' · ' + m.note : ''}`,
              onPick: () => swap(m),
            }))}
          />
        ) : null}
      />

      {/* Layout editing — duplicated from InverterEditor so users
          can change strings / panels-per-string / add MPPTs without
          having to navigate back to the inverter. Only meaningful
          when we have an inverter context (invId). */}
      {invId && (
        <>
          <div className="props-divider">String layout</div>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-faint)', marginBottom: 8, lineHeight: 1.5 }}>
            Bump <strong>Strings</strong> to add a parallel string on the same MPPT, or use <strong>+ Add MPPT</strong> to add a new MPPT lane.
          </div>
          <MpptLayoutEditor invId={invId} />
        </>
      )}

      <div className="props-divider">Status</div>
      <ExistingToggle
        checked={(activePanel as any).isExisting === true}
        onChange={(next) => {
          pushHistory();
          applyTopologyAndRelayout((t) => {
            // Per-inverter scope: only this inverter's panels flip
            // status. setExistingOnInverterPanels forks the panel type
            // if it's shared with other inverters so siblings don't
            // bleed.
            const targetIds = new Set<string>(invId ? [invId] : []);
            // No invId means we're editing the global "default" — fall
            // back to flipping the first type in place.
            if (targetIds.size === 0) {
              const types = (t.panelTypes && t.panelTypes.length > 0)
                ? t.panelTypes.slice()
                : (t.panels ? [{ ...t.panels, id: 'default' } as PanelTypeShape] : []);
              const updated = types.map((tp, i) =>
                i === 0 ? { ...tp, isExisting: next } : tp
              );
              return { ...t, panelTypes: updated, panels: updated[0] ?? t.panels };
            }
            return setExistingOnInverterPanels(t, targetIds, next);
          });
        }}
      />

      <div className="props-divider">Specs</div>
      <div className="props-row"><label>P_MAX</label><div className="props-position">{activePanel.pmax} W</div></div>
      <div className="props-row"><label>V_OC</label><div className="props-position">{activePanel.voc} V</div></div>
      <div className="props-row"><label>V_MP</label><div className="props-position">{activePanel.vmp} V</div></div>
      <div className="props-row"><label>I_SC</label><div className="props-position">{activePanel.isc} A</div></div>
      <div className="props-row"><label>I_MP</label><div className="props-position">{activePanel.imp} A</div></div>
      <div className="props-row"><label>Fuse rating</label><div className="props-position">{activePanel.fuseRating} A</div></div>
    </div>
  );
}

/** Shape of a registered panel type — kept narrow on purpose. */
type PanelTypeShape = {
  id: string;
  manufacturer: string; model: string;
  pmax: number; voc: number; vmp: number;
  isc: number; imp: number; fuseRating: number;
  isExisting?: boolean;
  label?: string;
};

/**
 * Shared "set isExisting on the panels owned by a SET of inverters"
 * routine. Same mutate-vs-fork pattern as the catalog swap:
 *   - If every inverter using a given panelTypeId is in the affected
 *     set, flip the type in place (no proliferation).
 *   - Otherwise, fork a new panel type with the desired flag and
 *     reassign just the affected inverters to the new type.
 *
 * Without this, toggling EXISTING on one inverter's panels would bleed
 * across to OTHER inverters that share the same panelTypeId — the
 * "items getting marked existing without being selected" bug.
 *
 * Mutates a copy of the topology and returns the new shape. Callers
 * should pass this through `applyTopologyAndRelayout` (or an equivalent)
 * so the panel-string nodes get rebuilt with the right flag.
 */
function setExistingOnInverterPanels(
  t: any,
  affectedInverterIds: Set<string>,
  next: boolean
): any {
  if (affectedInverterIds.size === 0) return t;

  // Materialise default type from legacy `panels` if no panelTypes yet.
  let types: PanelTypeShape[] = (t.panelTypes && t.panelTypes.length > 0)
    ? [...t.panelTypes]
    : (t.panels ? [{ ...t.panels, id: 'default' } as PanelTypeShape] : []);
  if (types.length === 0) return t;

  const defaultId = types[0].id;
  const inverters = [...t.inverters];

  // Group inverters by their effective panelTypeId.
  const refsByType = new Map<string, string[]>();
  for (const inv of inverters) {
    const tid = inv.panelTypeId ?? defaultId;
    if (!refsByType.has(tid)) refsByType.set(tid, []);
    refsByType.get(tid)!.push(inv.id);
  }

  const reassign = new Map<string, string>();   // invId -> newTypeId

  for (const [tid, invIds] of refsByType) {
    const affectedHere = invIds.filter((id) => affectedInverterIds.has(id));
    if (affectedHere.length === 0) continue;

    const allAffected = affectedHere.length === invIds.length;
    const tp = types.find((x) => x.id === tid);
    if (!tp) continue;

    if (allAffected) {
      // Every inverter using this type is in the affected set — mutate
      // the type in place; no fork needed.
      types = types.map((x) => x.id === tid ? { ...x, isExisting: next } : x);
    } else {
      // Some inverters share this type with others — fork a new type
      // for the affected ones so the unaffected siblings keep their
      // original flag.
      const used = new Set(types.map((x) => x.id));
      let candidate = `pt${types.length + 1}`;
      let n = types.length + 2;
      while (used.has(candidate)) { candidate = `pt${n}`; n++; }
      types.push({ ...tp, id: candidate, isExisting: next });
      for (const id of affectedHere) reassign.set(id, candidate);
    }
  }

  const updatedInverters = inverters.map((i) =>
    reassign.has(i.id) ? { ...i, panelTypeId: reassign.get(i.id)! } : i
  );

  // GC: drop any panel types nobody references.
  const referenced = new Set(updatedInverters.map((i) => i.panelTypeId ?? defaultId));
  types = types.filter((tp) => referenced.has(tp.id));

  return {
    ...t,
    inverters: updatedInverters,
    panelTypes: types,
    panels: types[0] ?? t.panels,
  };
}

// ============================================================
// SUB-BOARD CONFIG
// ============================================================

function SubBoardConfig() {
  const switchgear = useEditorStore((s) => s.topology.switchgear);
  const applyTopologyAndRelayout = useEditorStore((s) => s.applyTopologyAndRelayout);

  const subBoardPresent = switchgear.subBoard?.present ?? false;
  const solarVia        = switchgear.solarConnectionPoint ?? 'main';

  const togglePresent = (next: boolean) => {
    applyTopologyAndRelayout((t) => ({
      ...t,
      switchgear: {
        ...t.switchgear,
        subBoard: { present: next, label: t.switchgear.subBoard?.label ?? 'SUB-BOARD' },
        solarConnectionPoint: next ? (t.switchgear.solarConnectionPoint ?? 'main') : 'main',
      },
    }));
  };

  const setSolarVia = (next: 'main' | 'sub') => {
    applyTopologyAndRelayout((t) => ({
      ...t,
      switchgear: { ...t.switchgear, solarConnectionPoint: next },
    }));
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
        Sub-Board (SDB)
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginBottom: 4 }}>
        <input type="checkbox" checked={subBoardPresent} onChange={(e) => togglePresent(e.target.checked)} />
        Property has a sub-distribution board
      </label>
      {subBoardPresent && (
        <div style={{ marginLeft: 18, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Solar connects to:</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            <input type="radio" name="solarVia" value="main" checked={solarVia === 'main'} onChange={() => setSolarVia('main')} />
            Main switchboard (MSB)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            <input type="radio" name="solarVia" value="sub" checked={solarVia === 'sub'} onChange={() => setSolarVia('sub')} />
            Sub-board (SDB) — adds an SDB main switch in the diagram
          </label>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SURGE PROTECTION (SPD) CONFIG
// ============================================================

function SurgeProtectionConfig() {
  const switchgear = useEditorStore((s) => s.topology.switchgear);
  const applyTopologyAndRelayout = useEditorStore((s) => s.applyTopologyAndRelayout);

  const set = (key: 'dcSpd' | 'acSpd', next: boolean) => {
    applyTopologyAndRelayout((t) => ({
      ...t,
      switchgear: { ...t.switchgear, [key]: next },
    }));
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
        Surge protection (SPD)
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginBottom: 4 }}>
        <input type="checkbox" checked={switchgear.dcSpd === true} onChange={(e) => set('dcSpd', e.target.checked)} />
        DC SPD at each inverter (before the rooftop DC isolator)
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
        <input type="checkbox" checked={switchgear.acSpd === true} onChange={(e) => set('acSpd', e.target.checked)} />
        AC SPD at the main switchboard (after the main switch)
      </label>
      <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
        Need one somewhere else? Drag "Surge Protector (SPD)" from the Protection palette.
      </div>
    </div>
  );
}

// ============================================================
// SHARED UI: BrandBadge, ModelCard, CatalogPicker, LogoUpload
// ============================================================

const BRAND_COLOURS: Record<string, string> = {
  sungrow:        '#dc2626',
  sma:            '#0ea5e9',
  fronius:        '#f97316',
  goodwe:         '#0891b2',
  solaredge:      '#dc2626',
  huawei:         '#dc2626',
  tesla:          '#0f172a',
  enphase:        '#16a34a',
  'jinko solar':  '#1e40af',
  'longi solar':  '#7c3aed',
  'ja solar':     '#dc2626',
  'trina solar':  '#dc2626',
  'canadian solar': '#dc2626',
  rec:            '#16a34a',
  sunpower:       '#1e40af',
  'alpha-ess':    '#7c3aed',
  sonnen:         '#f59e0b',
  byd:            '#dc2626',
  pylontech:      '#0891b2',
};

function hashColour(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  const palette = ['#0f172a', '#1e40af', '#dc2626', '#7c3aed', '#0891b2', '#16a34a', '#f59e0b', '#db2777'];
  return palette[Math.abs(h) % palette.length];
}

function BrandBadge({
  manufacturer,
  size = 32,
  kind,
}: {
  manufacturer: string;
  size?: number;
  /** Optional product type — when set, looks up a real product
   *  logo from tbl_logos via /sld-api/brand-logos.php and renders
   *  it instead of the initials fallback. Same two-tier behaviour
   *  as autoLayout's brandBadgeSvg. */
  kind?: 'panels' | 'inverters' | 'batteries';
}) {
  // Subscribe to the manifest-loaded notifier so this component
  // re-renders once the backend manifest lands. Without this the
  // first render shows initials and never upgrades to the real
  // logo until something else triggers a re-render.
  const [, setTick] = useState(0);
  React.useEffect(() => {
    if (!kind) return;
    return onBrandLogosLoaded(() => setTick((t) => t + 1));
  }, [kind]);

  const logoUrl = kind ? getBrandLogoUrl(kind, manufacturer) : undefined;
  if (logoUrl) {
    return (
      <div
        style={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: 6,
          background: '#ffffff',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img
          src={logoUrl}
          alt={manufacturer}
          style={{
            maxWidth: '85%',
            maxHeight: '85%',
            objectFit: 'contain',
          }}
        />
      </div>
    );
  }

  // Initials fallback.
  const key = manufacturer.toLowerCase();
  const colour = BRAND_COLOURS[key] ?? hashColour(key);
  const words = manufacturer.replace(/[-_]/g, ' ').split(/\s+/).filter(Boolean);
  let initials = '';
  if (words.length === 1) {
    initials = words[0].slice(0, words[0].length <= 3 ? words[0].length : 2).toUpperCase();
  } else {
    initials = (words[0][0] + words[1][0]).toUpperCase();
  }
  const fontSize = initials.length >= 3 ? size * 0.32 : size * 0.42;
  return (
    <div style={{
      width: size, height: size, flexShrink: 0,
      borderRadius: 6, background: colour, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize, fontFamily: 'system-ui, sans-serif',
      letterSpacing: 0.5,
    }}>
      {initials}
    </div>
  );
}

function ModelCard({
  manufacturer, model, sub, onOpenPicker, picker, kind,
}: {
  manufacturer: string;
  model: string;
  sub: string;
  onOpenPicker: () => void;
  picker: React.ReactNode | null;
  kind?: 'panels' | 'inverters' | 'batteries';
}) {
  if (picker) return <>{picker}</>;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: 10, marginBottom: 12,
      border: '1px solid #cbd5e1', borderRadius: 8, background: '#f8fafc',
    }}>
      <BrandBadge manufacturer={manufacturer} size={44} kind={kind} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>{model}</div>
        <div style={{ color: '#475569', fontSize: 11 }}>{manufacturer}</div>
        <div style={{ color: '#64748b', fontSize: 10.5, marginTop: 2 }}>{sub}</div>
      </div>
      <button
        type="button" onClick={onOpenPicker}
        style={{
          padding: '6px 12px', fontSize: 11, fontWeight: 600,
          color: '#0f172a', background: '#fff',
          border: '1px solid #94a3b8', borderRadius: 6,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Change…
      </button>
    </div>
  );
}

function CatalogPicker({
  placeholder, filter, onFilter, onCancel, items, kind, onCustomInverter,
}: {
  placeholder: string;
  filter: string;
  onFilter: (s: string) => void;
  onCancel: () => void;
  items: Array<{
    key: string; manufacturer: string; model: string; sub: string;
    disabled?: boolean; disabledReason?: string; onPick: () => void;
  }>;
  kind?: 'panels' | 'inverters' | 'batteries';
  /** When set, the picker offers "Enter a model manually" so a user is
   *  never stuck because their inverter isn't in the starter catalog. */
  onCustomInverter?: (entry: InverterCatalogEntry) => void;
}) {
  const [custom, setCustom] = useState(false);
  if (custom && onCustomInverter) {
    return (
      <CustomInverterForm
        initialModel={filter}
        onSubmit={(e) => { setCustom(false); onCustomInverter(e); }}
        onCancel={() => setCustom(false)}
      />
    );
  }
  return (
    <div style={{
      border: '1px solid #cbd5e1', borderRadius: 8, padding: 8,
      background: '#fff', marginBottom: 12,
      boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
    }}>
      <input
        type="text" autoFocus placeholder={placeholder} value={filter}
        onChange={(e) => onFilter(e.target.value)}
        style={{
          width: '100%', padding: '6px 10px', fontSize: 12, marginBottom: 6,
          border: '1px solid #cbd5e1', borderRadius: 4, outline: 'none',
        }}
      />
      <div style={{ maxHeight: 280, overflowY: 'auto', margin: '0 -2px' }}>
        {items.length === 0 && (
          <div style={{ padding: 12, fontSize: 11, color: '#64748b', textAlign: 'center' }}>
            No matches — try a different search
          </div>
        )}
        {items.map((it) => (
          <button
            key={it.key} type="button" onClick={it.onPick} disabled={it.disabled}
            title={it.disabledReason ?? ''}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', textAlign: 'left',
              padding: '8px 10px', fontSize: 11,
              background: 'transparent', border: 'none', borderRadius: 6,
              cursor: it.disabled ? 'not-allowed' : 'pointer',
              opacity: it.disabled ? 0.45 : 1,
              marginBottom: 2,
            }}
            onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <BrandBadge manufacturer={it.manufacturer} size={32} kind={kind} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 12 }}>{it.model}</div>
              <div style={{ color: '#475569', fontSize: 10.5 }}>{it.manufacturer}</div>
              <div style={{ color: '#64748b', fontSize: 10.5, marginTop: 1 }}>{it.sub}</div>
            </div>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: onCustomInverter ? 'space-between' : 'flex-end', alignItems: 'center', marginTop: 6 }}>
        {onCustomInverter && (
          <button
            type="button" className="btn-ghost" onClick={() => setCustom(true)}
            style={{ fontSize: 11, padding: '4px 10px' }}
            title="Not in the list? Type the manufacturer, model and specs yourself."
          >
            Not listed? Enter manually
          </button>
        )}
        <button type="button" className="btn-ghost" onClick={onCancel} style={{ fontSize: 11, padding: '4px 12px' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * CustomInverterForm - manual entry for an inverter the starter catalog
 * doesn't have (customer-reported 2026-11-04: couldn't change a quote-
 * loaded SigenStor to the EC 20.0 TP, or add two existing Fronius
 * Symo M units, because none were in the catalog). Produces an
 * InverterCatalogEntry so the existing swap / add paths work unchanged.
 */
function CustomInverterForm({
  initialModel, onSubmit, onCancel,
}: {
  initialModel?: string;
  onSubmit: (entry: InverterCatalogEntry) => void;
  onCancel: () => void;
}) {
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState(initialModel ?? '');
  const [kw, setKw] = useState('');
  const [phases, setPhases] = useState<'1' | '3'>('3');
  const [type, setType] = useState<InverterCatalogEntry['type']>('string');
  const [mppts, setMppts] = useState('2');
  const [amps, setAmps] = useState('');
  const [vMin, setVMin] = useState('');
  const [vMax, setVMax] = useState('');

  const kwNum = parseFloat(kw);
  const mpptNum = parseInt(mppts, 10);
  const valid = manufacturer.trim() && model.trim() && kwNum > 0 && mpptNum >= 1 && mpptNum <= 12;

  const submit = () => {
    if (!valid) return;
    const a = parseFloat(amps);
    const lo = parseFloat(vMin);
    const hi = parseFloat(vMax);
    onSubmit({
      manufacturer: manufacturer.trim(),
      model: model.trim(),
      capacity: Math.round(kwNum * 1000),
      phases: phases === '3' ? 3 : 1,
      type,
      mpptCount: mpptNum,
      maxCurrentPerMppt: a > 0 ? a : 16,
      voltageRange: [lo > 0 ? lo : 150, hi > lo && hi > 0 ? hi : 1000],
      defaultStringsPerMppt: 1,
      note: 'Entered manually',
    });
  };

  const field = (label: string, el: React.ReactNode) => (
    <div className="props-row" style={{ marginBottom: 6 }}>
      <label style={{ fontSize: 10.5 }}>{label}</label>
      {el}
    </div>
  );
  const inp = (v: string, set: (s: string) => void, ph: string, num = false) => (
    <input
      type={num ? 'number' : 'text'} value={v} placeholder={ph}
      onChange={(e) => set(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
      style={{ width: '100%', fontSize: 12 }}
    />
  );

  return (
    <div style={{
      border: '1px solid #cbd5e1', borderRadius: 8, padding: 10,
      background: '#fff', marginBottom: 12,
      boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Enter inverter manually</div>
      {field('Manufacturer', inp(manufacturer, setManufacturer, 'e.g. Fronius'))}
      {field('Model', inp(model, setModel, 'e.g. Symo 20.0-3-M'))}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {field('AC power (kW)', inp(kw, setKw, '20', true))}
        {field('Phases', (
          <select value={phases} onChange={(e) => setPhases(e.target.value as '1' | '3')} style={{ width: '100%', fontSize: 12 }}>
            <option value="1">1φ</option>
            <option value="3">3φ</option>
          </select>
        ))}
        {field('Type', (
          <select value={type} onChange={(e) => setType(e.target.value as InverterCatalogEntry['type'])} style={{ width: '100%', fontSize: 12 }}>
            <option value="string">String (PV only)</option>
            <option value="hybrid">Hybrid (battery port)</option>
            <option value="microinverter">Microinverter</option>
          </select>
        ))}
        {field('MPPTs', inp(mppts, setMppts, '2', true))}
        {field('Max A per MPPT', inp(amps, setAmps, '16', true))}
        {field('MPPT range (V)', (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {inp(vMin, setVMin, '200', true)}
            <span style={{ fontSize: 11, color: '#64748b' }}>-</span>
            {inp(vMax, setVMax, '1000', true)}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
        <button type="button" className="btn-ghost" onClick={onCancel} style={{ fontSize: 11, padding: '4px 12px' }}>Back</button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid} style={{ fontSize: 11, padding: '4px 12px' }}>Use this inverter</button>
      </div>
    </div>
  );
}

function LogoUpload({
  current, onPick, onClear,
}: {
  current?: string;
  onPick: (dataUrl: string) => void;
  onClear: () => void;
}) {
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) {
      void useEditorStore.getState().askConfirm({
        title: 'Logo file is too large',
        message:
          `That file is ${(file.size / 1024).toFixed(0)} KB. Please use a file under 500 KB — PNG or SVG works best for crisp print output.`,
        singleButton: true,
        tone: 'warning',
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') onPick(result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {current && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: 4, border: '1px solid #e2e8f0', borderRadius: 4, background: '#f8fafc',
        }}>
          <img
            src={current} alt="Installer logo"
            style={{ height: 30, width: 'auto', objectFit: 'contain', background: '#fff' }}
          />
          <button type="button" className="btn-ghost" onClick={onClear} style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px' }}>
            Remove
          </button>
        </div>
      )}
      <input
        type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
        onChange={handleFile} style={{ fontSize: 11 }}
      />
    </div>
  );
}

/**
 * WireEditor — RHS panel shown when the user clicks a single wire.
 * Adds a free-form cable label (e.g. "6mm² DC", "32A AC", "PE 16mm²")
 * to the edge's data, rendered on the canvas at the wire's midpoint
 * by SldWire's EdgeLabelRenderer block.
 *
 * Customer ask: "Can you add the ability to label the connecting
 * cables as well as the equipment" — equipment already has Label /
 * Sub-label inputs in the generic symbol editor, this is the wire
 * equivalent. Single edge only; multi-wire bulk-edit isn't a real
 * use case yet (Canvas falls through to no-selection for that).
 */
function WireEditor({ edgeId }: { edgeId: string }) {
  const setEdges = useEditorStore((s) => s.setEdges);
  const pushHistory = useEditorStore((s) => s.pushHistory);
  const edge = useEditorStore((s) => s.edges.find((e) => e.id === edgeId));

  if (!edge) {
    return (
      <div className="props-panel">
        <div className="props-empty">
          <span className="props-hint">Wire not found.</span>
        </div>
      </div>
    );
  }

  const label = edge.data?.label ?? '';
  const style = edge.data?.style ?? 'any';

  const setLabel = (value: string) => {
    setEdges((prev) =>
      prev.map((e) =>
        e.id === edgeId
          ? { ...e, data: { ...e.data, label: value } }
          : e
      )
    );
  };

  const clearLabel = () => {
    pushHistory();
    setEdges((prev) =>
      prev.map((e) => {
        if (e.id !== edgeId) return e;
        // Drop the key entirely rather than storing an empty string
        // so persisted state stays tidy and SldWire's `data?.label &&`
        // check fires correctly.
        const { label: _drop, ...restData } = (e.data ?? {}) as { label?: string } & Record<string, unknown>;
        void _drop;
        return { ...e, data: restData };
      })
    );
  };

  const deleteWire = () => {
    pushHistory();
    setEdges((prev) => prev.filter((e) => e.id !== edgeId));
  };

  return (
    <div className="props-panel">
      <div className="props-header">Wire</div>
      <div className="props-sub" style={{ marginBottom: 8 }}>
        Style: <strong>{style.toUpperCase()}</strong> · ID: {edgeId}
      </div>

      <div className="props-row">
        <label>Cable label</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onFocus={pushHistory}
          placeholder='e.g. 6mm² DC · 32A AC · PE 16mm²'
          maxLength={64}
        />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-faint)', lineHeight: 1.5, marginBottom: 12 }}>
        Shows on the canvas at the wire's midpoint. Leave blank for no label.
      </div>

      {label && (
        <button
          type="button"
          className="btn-tertiary"
          onClick={clearLabel}
          style={{ fontSize: 11, marginBottom: 12 }}
        >
          Clear label
        </button>
      )}

      <button className="btn-danger" onClick={deleteWire}>
        Delete wire
      </button>
    </div>
  );
}
